import React, {
  type CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowLeft,
  FiBriefcase,
  FiChevronRight,
  FiClipboard,
  FiClock,
  FiExternalLink,
  FiHome,
  FiInfo,
  FiPhone,
  FiPrinter,
  FiX,
} from 'react-icons/fi';
import Select from 'react-select';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Circle,
  Container,
  Field,
  Flex,
  Grid,
  HStack,
  Input,
  Separator,
  Stack,
  Text,
  Textarea,
} from 'tcm-chakra-ui';
import { NavBar } from '../components/layout/NavBar';
import { AthenaLinkButton } from '../components/shared/AthenaLinkButton';
import { StartNewEncounterButton } from '../components/shared/StartNewEncounterButton';
import { StartOverConfirmModal } from '../components/shared/StartOverConfirmModal';
import { WrapCodeModal } from '../components/shared/WrapCodeModal';
import {
  FollowUpStepper,
  type WizardStep,
} from '../components/workflow/FollowUpStepper';
import { ProviderChoiceList } from '../components/workflow/ProviderChoiceList';
import { useEncounterSession } from '../context/encounterSessionContext';
import { useHospitalContext } from '../context/hospitalContext';
import type {
  PatientLookupFormValues,
  PatientLookupResult,
} from '../features/follow-up-flow/types';
import { buildAppointmentNotes, validateAppointmentNotes } from '../features/tcm-workflow/utils/appointmentNotes';
import { type ProviderOption, providerSelectKey } from '../features/tcm-workflow/providerOption';
import { ALREADY_SCHEDULED_WRAP_CODE } from '../features/tcm-workflow/wrapCodes';
import { useApiClient } from '../services/api/axiosClient';
import { getAxiosErrorMessage, getAxiosErrorStatus } from '../services/api/httpHelpers';
import { analytics } from '../../../utils/analytics';
import type {
  AlreadyScheduled,
  AthenaPatient,
  AvailableSlot,
  GenericResponse,
  Practice,
  ProviderSearchResponse,
} from '../services/api/swaggerSchemas';
import {
  fetchDirectPatient,
  findPatient,
  postEncounterOutcome,
  postSelectedPcp,
  providerSearch,
  scheduleAppointment,
} from '../services/api/tcmGatewayService';
import { isPacUser } from '../../../utils/permissionUtils';
import { formatSlotForPrint, openPrintSummary } from '../features/tcm-workflow/utils/printSummary';

/** Hospital follow-up appointment request (after-hours) — Formstack, opens in a new tab */
const WRAP_CODE_OUTCOME_ENABLED =
  import.meta.env.VITE_WRAP_CODE_OUTCOME_ENABLED === 'true';

const AFTER_HOURS_FORM_URL =
  'https://communityhealthsystems.formstack.com/forms/hospital_follow_up_appt_request_form';

function resolveIsNewPatientFromSearch(
  response: ProviderSearchResponse,
): boolean {
  if (typeof response.is_new_patient === 'boolean') {
    return response.is_new_patient;
  }
  return !response.providers.some((p) => p.is_patient_pcp === true);
}

/**
 * Only pass Athena patient_id / tablespace to provider search when the clerk confirmed
 * the chart and Athena reported a match. Rejected or “not found” flows must not send a
 * possibly wrong id to list_provider (same as having no id).
 */
function trustedAthenaContextForProviderSearch(
  athenaPatient: AthenaPatient | null,
  skippedPatientVerification: boolean,
): { patientId: string | null; tableSpace: string | null } {
  if (
    skippedPatientVerification ||
    athenaPatient == null ||
    athenaPatient.match_found === false
  ) {
    return { patientId: null, tableSpace: null };
  }

  const rawId = athenaPatient.patient_id;
  const id =
    rawId != null && String(rawId).trim() !== ''
      ? String(rawId).trim()
      : null;

  const rawTs = athenaPatient.athena_tablespace;
  const tableSpace =
    rawTs != null && String(rawTs).trim() !== ''
      ? String(rawTs).trim()
      : null;

  return { patientId: id, tableSpace };
}

type WizardSummaryProps = {
  submittedValues: PatientLookupFormValues | null;
  lookupResults: PatientLookupResult[];
  athenaPatient: AthenaPatient | null;
};

type PatientCheckData = {
  fullName: string;
  dateOfBirth: string;
  phoneNumber: string;
  address: string;
  insuranceProvider: string;
  insuranceId: string;
  athenaPatientId: string;
  athenaTableSpace: string;
  emrLink?: string | null;
  matchFound: boolean;
};

function displayOrDash(value: string | undefined | null): string {
  const s = value?.trim() ?? '';
  return s || '—';
}

function displayOrNone(value: string | undefined | null): string {
  const s = value?.trim() ?? '';
  return s || 'None on file';
}

function formatPhoneForDisplay(
  value: string | undefined | null,
): string {
  const raw = (value ?? '').trim();
  if (!raw) return 'None on file';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function phoneHref(value: string | undefined | null): string {
  const raw = (value ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  return digits ? `tel:${digits}` : `tel:${raw}`;
}

function splitAddressLines(
  value: string | undefined | null,
): [string, string | null] {
  const raw = (value ?? '').trim();
  if (!raw) return ['None on file', null];
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw, null];
  return [parts[0], parts.slice(1).join(', ')];
}

function getInitials(value: string | undefined | null): string {
  const words = (value ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'NA';
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

function formatDistanceLabel(
  value: number | null | undefined,
): string {
  if (value == null || Number.isNaN(Number(value)))
    return 'Distance unavailable';
  const n = Number(value);
  return `${n >= 100 ? n.toFixed(0) : n.toFixed(1)} mi from patient`;
}


type SlotSelectOption = {
  value: string;
  label: string;
};


function resolvePracticeForProvider(
  provider: ProviderOption | undefined,
  practices: Practice[],
): Practice | undefined {
  if (!provider) return undefined;
  if (provider.practiceRecord) return provider.practiceRecord;
  if (provider.practiceId) {
    const byId = practices.find((p) => p.id === provider.practiceId);
    if (byId) return byId;
  }
  if (practices.length === 1) return practices[0];
  return undefined;
}

function slotFollowUpWindowLabel(
  provider: ProviderOption | undefined,
  slot: AvailableSlot | null | undefined,
): string | null {
  if (!provider || !slot) return null;
  const inWin = (provider.availableSlotsInWindow ?? []).some(
    (s) => s.id === slot.id,
  );
  return inWin ? 'In follow-up window' : 'Outside follow-up window';
}

function PracticeInfoPanel({
  practice,
  compact = false,
  hideLabel = false,
  useBackline = true,
}: {
  practice: Practice | undefined;
  compact?: boolean;
  hideLabel?: boolean;
  useBackline?: boolean;
}) {
  if (!practice) {
    return (
      <Text color="text.secondary" fontSize="sm">
        No practice details were returned for this provider.
      </Text>
    );
  }
  return (
    <Box
      borderRadius="md"
      borderWidth={hideLabel ? '0' : '1px'}
      borderColor="border.subtle"
      bg={hideLabel ? 'transparent' : 'background.surfaceTertiary'}
      px={compact ? 3 : 4}
      py={compact ? 2 : 3}
    >
      {hideLabel ? null : (
        <Text
          fontSize="xs"
          fontWeight="700"
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb={2}
        >
          Practice
        </Text>
      )}
      <Stack gap={1}>
        <Text
          color="text.brandDark"
          fontWeight="700"
          fontSize={compact ? 'sm' : 'md'}
        >
          {practice.name}
        </Text>
        {practice.location ? (
          <Text color="text.secondary" fontSize="sm">
            {practice.location}
            {practice.address ? ` · ${practice.address}` : ''}
          </Text>
        ) : null}
        {(() => {
          const phone = useBackline
            ? practice.backline?.trim() || practice.phone?.trim()
            : practice.phone?.trim();

          return phone ? (
            <Text color="text.secondary" fontSize="sm">
              {phone}
            </Text>
          ) : null;
        })()}
      </Stack>
    </Box>
  );
}

function formatProviderSlotTime(
  slot: AvailableSlot | null | undefined,
): string {
  if (!slot) return '';

  const date = new Date(slot.start_time);
  if (Number.isNaN(date.getTime())) {
    return slot.start_time;
  }

  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function collectSlotsForProvider(
  provider: ProviderOption,
): AvailableSlot[] {
  const inWindow = provider.availableSlotsInWindow ?? [];
  const outside = provider.firstAvailableSlotOutsideWindow;
  const list = [...inWindow];
  if (outside && !inWindow.some((s) => s.id === outside.id)) {
    list.push(outside);
  }
  return list;
}

function sortSlotsChronologically(
  slots: AvailableSlot[],
): AvailableSlot[] {
  return [...slots].sort((a, b) => {
    const ta = Date.parse(a.start_time);
    const tb = Date.parse(b.start_time);
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return ta - tb;
  });
}

function getTodayDateInputValue(): string {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - tzOffsetMs)
    .toISOString()
    .slice(0, 10);
}

const outlineButtonCss: CSSProperties = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--tcm-colors-border-gray, #CBD5E0)',
  borderRadius: '0.375rem',
  paddingLeft: '1.25rem',
  paddingRight: '1.25rem',
  minHeight: '2.5rem',
  fontWeight: 600,
  backgroundColor: 'var(--tcm-colors-background-primary)',
  color: 'var(--tcm-colors-text-brandDark, #1A305D)',
};

/** Same provider as UI selection: match by composite select key (id|practiceId). */
function resolveWizardProvider(
  options: ProviderOption[],
  selectedKey: string,
): ProviderOption | undefined {
  if (!selectedKey) return undefined;
  return options.find((p) => providerSelectKey(p) === selectedKey);
}

function resolveScheduleTablespace(
  slot: AvailableSlot,
  provider: ProviderOption | undefined,
  athena: AthenaPatient | null,
): string {
  const fromSlot = slot.tablespace?.trim();
  if (fromSlot) return fromSlot;
  const fromProvider = provider?.tablespace?.trim();
  if (fromProvider) return fromProvider;
  const fromPatient = athena?.athena_tablespace?.trim();
  return fromPatient ?? '';
}

function VisibleInlineSpinner({
  size = '1rem',
  thickness = '2px',
  mt,
}: {
  size?: string;
  thickness?: string;
  mt?: number;
}) {
  return (
    <Box
      w={size}
      h={size}
      mt={mt}
      borderRadius="full"
      borderWidth={thickness}
      borderStyle="solid"
      borderColor="var(--tcm-colors-border-gray)"
      borderTopColor="var(--tcm-colors-brand-500)"
      css={{ animation: 'tcm-spin 0.8s linear infinite' }}
      aria-label="Loading"
      flexShrink={0}
    />
  );
}

function WizardSummary({
  submittedValues,
  lookupResults,
  athenaPatient,
}: WizardSummaryProps) {
  return (
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
      <Card.Root bg="background.surfaceTertiary" borderRadius="xl">
        <Card.Body>
          <Text color="text.secondary" fontSize="sm">
            Patient Name
          </Text>
          <Text color="text.brandDark" fontWeight="700" mt={1}>
            {submittedValues?.patientName}
          </Text>
        </Card.Body>
      </Card.Root>

      <Card.Root bg="background.surfaceTertiary" borderRadius="xl">
        <Card.Body>
          <Text color="text.secondary" fontSize="sm">
            Date of Birth
          </Text>
          <Text color="text.brandDark" fontWeight="700" mt={1}>
            {submittedValues?.dateOfBirth}
          </Text>
        </Card.Body>
      </Card.Root>

      <Card.Root bg="background.surfaceTertiary" borderRadius="xl">
        <Card.Body>
          <Text color="text.secondary" fontSize="sm">
            Estimated Discharge Date
          </Text>
          <Text color="text.brandDark" fontWeight="700" mt={1}>
            {submittedValues?.estimatedDischargeDate}
          </Text>
        </Card.Body>
      </Card.Root>

      <Card.Root bg="background.surfaceTertiary" borderRadius="xl">
        <Card.Body>
          <Text color="text.secondary" fontSize="sm">
            Lookup Result
          </Text>
          <Text color="text.brandDark" fontWeight="700" mt={1}>
            {lookupResults[0]?.patientName || 'No patient returned'}
          </Text>
          <Text color="text.secondary" fontSize="sm" mt={1}>
            {lookupResults[0]?.confidenceLabel ??
              'Patient lookup result'}
          </Text>
          <HStack mt={3}>
            <AthenaLinkButton
              patientId={athenaPatient?.patient_id}
              tableSpace={athenaPatient?.athena_tablespace}
              emrLink={athenaPatient?.emr_link}
            />
          </HStack>
        </Card.Body>
      </Card.Root>
    </Grid>
  );
}

type PatientCheckStepProps = {
  data: PatientCheckData;
  onGoBack: () => void;
  onContinue: () => void;
  onContinueAnyway: () => void;
};

function PatientCheckStep({
  data,
  onGoBack,
  onContinue,
  onContinueAnyway,
}: PatientCheckStepProps) {
  const isMatchFound = data.matchFound;
  const primaryAction = isMatchFound ? onContinue : onContinueAnyway;
  const [addressLine1, addressLine2] = splitAddressLines(
    data.address,
  );
  const formattedPhone = formatPhoneForDisplay(data.phoneNumber);
  const patientInitials = getInitials(data.fullName);

  return (
    <Stack gap={6}>
      <Stack gap={1}>
        <Text color="text.brandDark" fontSize="2xl" fontWeight="700">
          Confirm patient
        </Text>
        <Text color="text.secondary" fontSize="sm">
          Review the details below. If this is the right patient,
          continue to search for providers.
        </Text>
      </Stack>

      <Card.Root
        bg="background.primary"
        borderRadius="lg"
        border="1px solid"
        borderColor="border.gray"
        boxShadow="sm"
      >
        <Card.Body p={{ base: 5, md: 6 }}>
          <Stack gap={5}>
            <Flex
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              gap={4}
              wrap="wrap"
            >
              <HStack gap={3} align="center" minW={0}>
                <Circle
                  size="2.75rem"
                  bg="blue.100"
                  color="text.brandLight"
                  fontWeight="700"
                >
                  {patientInitials}
                </Circle>
                <Stack gap={0} minW={0}>
                  <Text
                    color="text.brandDark"
                    fontSize="2xl"
                    fontWeight="700"
                    lineClamp={1}
                  >
                    {displayOrNone(data.fullName)}
                  </Text>
                </Stack>
              </HStack>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                borderWidth="1px"
                borderColor={
                  isMatchFound ? 'green.200' : 'orange.200'
                }
                bg={isMatchFound ? 'green.100' : 'orange.100'}
                color={isMatchFound ? 'green.800' : 'orange.800'}
                fontWeight="700"
              >
                {isMatchFound ? 'Athena match' : 'No Athena match'}
              </Badge>
            </Flex>

            <Grid
              templateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={{ base: 6, md: 8 }}
            >
              <Stack gap={5}>
                <Text
                  color="text.brandLight"
                  fontSize="xs"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Demographics
                </Text>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Full name
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {displayOrDash(data.fullName)}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Date of birth
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {displayOrDash(data.dateOfBirth)}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Phone
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {formattedPhone}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Address
                  </Text>
                  <Stack gap={0.5} mt={1}>
                    <Text color="text.primary" lineHeight="tall">
                      {addressLine1}
                    </Text>
                    {addressLine2 ? (
                      <Text color="text.primary" lineHeight="tall">
                        {addressLine2}
                      </Text>
                    ) : null}
                  </Stack>
                </Box>
              </Stack>

              <Stack
                gap={5}
                borderLeft={{
                  base: 'none',
                  md: '1px solid var(--tcm-colors-border-subtle)',
                }}
                pl={{ base: 0, md: 8 }}
              >
                <Text
                  color="text.brandLight"
                  fontSize="xs"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Insurance &amp; Athena
                </Text>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Insurance provider
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {displayOrNone(data.insuranceProvider)}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Insurance member ID
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {displayOrNone(data.insuranceId)}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Athena patient ID
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {isMatchFound
                      ? displayOrDash(data.athenaPatientId)
                      : 'Not found'}
                  </Text>
                </Box>
                <Box>
                  <Text
                    color="text.brandDark"
                    fontSize="sm"
                    fontWeight="700"
                  >
                    Athena tablespace
                  </Text>
                  <Text color="text.primary" mt={1}>
                    {displayOrDash(data.athenaTableSpace)}
                  </Text>
                </Box>
                {isMatchFound ? (
                  <Box>
                    <Text
                      color="text.brandDark"
                      fontSize="sm"
                      fontWeight="700"
                      mb={2}
                    >
                      Open in Athena
                    </Text>
                    <AthenaLinkButton
                      patientId={data.athenaPatientId}
                      tableSpace={data.athenaTableSpace}
                      emrLink={data.emrLink}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Grid>
          </Stack>
        </Card.Body>
      </Card.Root>

      <Stack gap={3}>
        <Button
          onClick={primaryAction}
          variant="ghost"
          justifyContent="space-between"
          h="auto"
          minH="5.75rem"
          px={{ base: 4, md: 5 }}
          py={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor="background.brandLight"
          bg="background.brandLight"
          color="text.white"
          _hover={{ bg: 'background.brandDark' }}
        >
          <Stack align="flex-start" gap={0.5}>
            <Text fontWeight="700" fontSize="lg">
              Confirm and search for providers
            </Text>
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.92)">
              This is the correct patient.
            </Text>
          </Stack>
          <FiChevronRight size={20} aria-hidden />
        </Button>

        <Button
          onClick={onContinueAnyway}
          variant="ghost"
          justifyContent="space-between"
          h="auto"
          minH="5.75rem"
          px={{ base: 4, md: 5 }}
          py={4}
          borderRadius="xl"
          borderWidth="1px"
          borderColor="border.gray"
          bg="background.surfaceTertiary"
          color="text.brandDark"
          _hover={{ bg: 'background.secondary' }}
        >
          <Stack align="flex-start" gap={0.5}>
            <Text fontWeight="700" fontSize="lg">
              Wrong patient, but I have a named PCP
            </Text>
            <Text fontSize="sm" color="text.secondary">
              Search for the correct provider to call.
            </Text>
          </Stack>
          <FiChevronRight size={20} aria-hidden />
        </Button>

        <button
          onClick={onGoBack}
          style={{
            alignSelf: 'center',
            color: 'var(--tcm-colors-text-secondary)',
            textDecoration: 'underline',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            marginTop: '0.25rem',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <FiArrowLeft size={14} aria-hidden />
          Go back and search again
        </button>
      </Stack>
    </Stack>
  );
}

function formatAlreadyScheduledDateTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return isoString;
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${weekday}, ${dateStr} at ${timeStr}`;
  } catch {
    return isoString;
  }
}

type ProviderSearchStepProps = {
  providers: ProviderOption[];
  selectedProviderId: string;
  onProviderIdChange: (id: string) => void;
  onBack: () => void;
  onContinue: (providerId?: string) => void;
  onStartOver: () => void;
  status: 'idle' | 'loading' | 'success' | 'error';
  errorMessage: string;
  pacPhone: string | null;
  /** When false (patient not confirmed in step 2), hide Established PCP labels and badges. */
  showEstablishedPcpUi: boolean;
  /** True when step 2 used "No, but Search for Providers Anyway" (patient not accepted). */
  patientNotAccepted: boolean;
  /** True when find-patient returned match_found: false (no Athena match). */
  patientNotFoundInAthena: boolean;
  /** When set, blocks scheduling and shows an already-scheduled notice. */
  alreadyScheduled: AlreadyScheduled | null;
  patientName: string;
};

function ProviderSearchStep({
  providers,
  selectedProviderId,
  onProviderIdChange,
  onBack,
  onContinue,
  onStartOver,
  status,
  errorMessage,
  pacPhone,
  showEstablishedPcpUi,
  patientNotAccepted,
  patientNotFoundInAthena,
  alreadyScheduled,
  patientName,
}: ProviderSearchStepProps) {
  const [isChangeFlow, setIsChangeFlow] = useState(false);

  const pcpOnFile = providers.find((p) => Boolean(p.isPatientPcp));
  const activeProvider =
    providers.find((p) => providerSelectKey(p) === selectedProviderId) ??
    pcpOnFile ??
    providers[0];
  const hasProviderOnFile = Boolean(pcpOnFile);
  const shouldStartInChangeFlow =
    patientNotAccepted ||
    patientNotFoundInAthena ||
    !showEstablishedPcpUi ||
    !hasProviderOnFile;
  const showChangeFlow = shouldStartInChangeFlow || isChangeFlow;
  const resolvedPacPhone = pacPhone?.trim() ?? '';

  const handleConfirmContinue = () => {
    if (!activeProvider?.id) return;
    const key = providerSelectKey(activeProvider);
    onProviderIdChange(key);
    onContinue(key);
  };

  return (
    <Stack gap={6}>
      <Stack gap={1}>
        <Text color="text.brandDark" fontSize="2xl" fontWeight="700">
          Confirm patient PCP
        </Text>
        <Text color="text.secondary" fontSize="sm">
          Choose the patient&apos;s PCP for this encounter. You will
          choose the scheduling provider and appointment time in the
          next step.
        </Text>
      </Stack>

      {status === 'success' ? (
        <Card.Root
          bg="background.primary"
          borderRadius="lg"
          border="1px solid"
          borderColor="border.gray"
          boxShadow="sm"
        >
          <Card.Body p={{ base: 5, md: 6 }}>
            <Stack gap={5}>
              {showChangeFlow ? (
                <Stack gap={4}>
                  {!hasProviderOnFile ? (
                    <HStack
                      gap={2}
                      wrap="wrap"
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="orange.300"
                      bg="orange.100"
                      color="orange.900"
                      px={4}
                      py={3}
                    >
                      <HStack gap={2}>
                        <Box display="inline-flex" aria-hidden>
                          <FiInfo size={16} />
                        </Box>
                        <Text fontWeight="700">No PCP on file</Text>
                      </HStack>
                      <Badge
                        px={2.5}
                        py={1}
                        borderRadius="full"
                        borderWidth="1px"
                        borderColor="orange.300"
                        bg="orange.200"
                        color="orange.900"
                        fontWeight="600"
                      >
                        Primary care
                      </Badge>
                    </HStack>
                  ) : null}

                  {hasProviderOnFile ? (
                    <HStack
                      justify="space-between"
                      align="flex-start"
                      gap={4}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor="border.subtle"
                      bg="background.surfaceTertiary"
                      px={4}
                      py={3}
                    >
                      <HStack gap={3} align="flex-start">
                        <Circle
                          size="1.75rem"
                          bg="red.100"
                          color="red.700"
                          flexShrink={0}
                        >
                          <FiX size={12} />
                        </Circle>
                        <Stack gap={0.5}>
                          <Text color="text.secondary" fontSize="sm">
                            Removing
                          </Text>
                          <Text
                            color="text.brandDark"
                            fontWeight="600"
                          >
                            {pcpOnFile?.name} —{' '}
                            {pcpOnFile?.department}
                          </Text>
                        </Stack>
                      </HStack>
                      {!shouldStartInChangeFlow ? (
                        <button
                          onClick={() => {
                            setIsChangeFlow(false);
                            if (pcpOnFile)
                              onProviderIdChange(providerSelectKey(pcpOnFile));
                          }}
                          style={{
                            color:
                              'var(--tcm-colors-text-brandLight)',
                            textDecoration: 'underline',
                            fontWeight: 600,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Undo
                        </button>
                      ) : null}
                    </HStack>
                  ) : null}

                  <Stack gap={1}>
                    <Text color="text.brandDark" fontWeight="700">
                      Look up provider by name
                    </Text>
                    <Text color="text.secondary" fontSize="sm">
                      If you have the provider&apos;s name from a
                      referral or patient records, open the list and
                      search below.
                    </Text>
                  </Stack>

                  <ProviderChoiceList
                    providers={providers}
                    selectedProviderId={selectedProviderId}
                    onChange={(id) => {
                      onProviderIdChange(id);
                    }}
                    compactTrigger
                  />
                  {!alreadyScheduled ? (
                    <HStack justify="flex-end" pt={1}>
                      <Button
                        colorPalette="brand"
                        variant="solid"
                        css={{
                          bg: 'background.brandLight',
                          color: 'text.white',
                          px: 6,
                          minH: 10,
                          borderRadius: 'md',
                          fontWeight: 'bold',
                        }}
                        disabled={!selectedProviderId}
                        onClick={() => {
                          if (!selectedProviderId) return;
                          onContinue(selectedProviderId);
                        }}
                      >
                        Confirm selected provider
                      </Button>
                    </HStack>
                  ) : null}

                  <Separator />

                  <Stack gap={1}>
                    <Text color="text.brandDark" fontWeight="700">
                      Don&apos;t have a provider name?
                    </Text>
                    <Text color="text.secondary" fontSize="sm">
                      {resolvedPacPhone ? (
                        <>
                          Call PAC at{' '}
                          <a
                            href={phoneHref(resolvedPacPhone)}
                            style={{
                              color:
                                'var(--tcm-colors-text-brandLight)',
                              textDecoration: 'underline',
                              fontWeight: 600,
                            }}
                          >
                            {formatPhoneForDisplay(resolvedPacPhone)}
                          </a>{' '}
                          for assistance, or{' '}
                        </>
                      ) : (
                        <>PAC phone not available. You can also </>
                      )}
                      <a
                        href={AFTER_HOURS_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'var(--tcm-colors-text-brandLight)',
                          textDecoration: 'underline',
                          fontWeight: 600,
                        }}
                      >
                        use the after-hours formstack
                      </a>
                      .
                    </Text>
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={4}>
                  <Box
                    borderRadius="xl"
                    borderWidth="2px"
                    borderColor="background.brandLight"
                    bg="background.surfaceTertiary"
                    overflow="hidden"
                  >
                    <Box px={{ base: 4, md: 5 }} py={4}>
                      <Flex
                        justify="space-between"
                        align="center"
                        gap={3}
                        wrap="wrap"
                      >
                        <HStack gap={3}>
                          <Circle
                            size="2.4rem"
                            bg="brand.50"
                            color="text.brandLight"
                            fontWeight="700"
                          >
                            {getInitials(activeProvider?.name)}
                          </Circle>
                          <Stack gap={0}>
                            <Text
                              color="text.brandDark"
                              fontWeight="700"
                              fontSize="2xl"
                            >
                              {activeProvider?.name ?? 'Provider'}
                            </Text>
                            <Text color="text.secondary">
                              {activeProvider?.specialty ??
                                'Primary care'}
                            </Text>
                          </Stack>
                        </HStack>
                      </Flex>
                    </Box>
                    <Separator />
                    <Stack px={{ base: 4, md: 5 }} py={4} gap={4}>
                      <Grid
                        templateColumns={{
                          base: '1fr',
                          md: '1fr 1fr',
                        }}
                        gap={4}
                      >
                        <Stack gap={0.5}>
                          <Text
                            color="text.secondary"
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Practice
                          </Text>
                          <Text
                            color="text.brandDark"
                            fontWeight="600"
                          >
                            {activeProvider?.department ?? 'Practice'}
                          </Text>
                        </Stack>
                        <Stack gap={0.5}>
                          <Text
                            color="text.secondary"
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Distance from patient
                          </Text>
                          <Text
                            color="text.brandDark"
                            fontWeight="600"
                          >
                            {formatDistanceLabel(
                              activeProvider?.distanceFromPatientMiles,
                            )}
                          </Text>
                        </Stack>
                        <Stack
                          gap={0.5}
                          gridColumn={{
                            base: '1 / -1',
                            md: '1 / -1',
                          }}
                        >
                          <Text
                            color="text.secondary"
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Address
                          </Text>
                          <Text
                            color="text.brandDark"
                            fontWeight="600"
                          >
                            {activeProvider?.practiceRecord
                              ?.address ??
                              activeProvider?.practiceRecord
                                ?.location ??
                              'None on file'}
                          </Text>
                        </Stack>
                      </Grid>
                      <HStack gap={2} wrap="wrap">
                        <Badge
                          px={2.5}
                          py={1}
                          borderRadius="full"
                          borderWidth="1px"
                          borderColor="green.200"
                          bg="green.100"
                          color="green.800"
                        >
                          Employed
                        </Badge>
                        <Badge
                          px={2.5}
                          py={1}
                          borderRadius="full"
                          borderWidth="1px"
                          borderColor="background.brandLight"
                          bg="brand.50"
                          color="text.brandLight"
                        >
                          Established PCP
                        </Badge>
                        <Badge
                          px={2.5}
                          py={1}
                          borderRadius="full"
                          borderWidth="1px"
                          borderColor="background.brandLight"
                          bg="brand.50"
                          color="text.brandLight"
                        >
                          Accepting new patients
                        </Badge>
                      </HStack>
                    </Stack>
                  </Box>

                  {!alreadyScheduled ? (
                    <Button
                      onClick={handleConfirmContinue}
                      variant="ghost"
                      justifyContent="space-between"
                      h="auto"
                      minH="5.75rem"
                      px={{ base: 4, md: 5 }}
                      py={4}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="background.brandLight"
                      bg="background.brandLight"
                      color="text.white"
                      _hover={{ bg: 'background.brandDark' }}
                    >
                      <Stack align="flex-start" gap={0.5}>
                        <Text fontWeight="700" fontSize="lg">
                          Confirm PCP and choose appointment
                        </Text>
                        <Text
                          fontSize="sm"
                          color="rgba(255, 255, 255, 0.92)"
                        >
                          Proceed to scheduling provider and time
                          selection.
                        </Text>
                      </Stack>
                      <FiChevronRight size={20} aria-hidden />
                    </Button>
                  ) : null}

                  <Button
                    onClick={() => setIsChangeFlow(true)}
                    variant="ghost"
                    justifyContent="space-between"
                    h="auto"
                    minH="5.75rem"
                    px={{ base: 4, md: 5 }}
                    py={4}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="border.gray"
                    bg="background.surfaceTertiary"
                    color="text.brandDark"
                    _hover={{ bg: 'background.secondary' }}
                  >
                    <Stack align="flex-start" gap={0.5}>
                      <Text fontWeight="700" fontSize="lg">
                        This isn&apos;t the right PCP
                      </Text>
                      <Text fontSize="sm" color="text.secondary">
                        Search for a different provider.
                      </Text>
                    </Stack>
                    <FiChevronRight size={20} aria-hidden />
                  </Button>
                </Stack>
              )}
              {alreadyScheduled ? (
                <Alert.Root status="warning" borderRadius="lg">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>Appointment already scheduled</Alert.Title>
                    <Alert.Description>
                      {patientName} has a{' '}
                      <strong>{alreadyScheduled.appointment_type}</strong>{' '}
                      already scheduled with{' '}
                      <strong>{alreadyScheduled.physician_name}</strong> at{' '}
                      <strong>{alreadyScheduled.facility_name}</strong> on{' '}
                      {formatAlreadyScheduledDateTime(alreadyScheduled.start)}.
                      Cannot schedule another.
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              ) : null}
            </Stack>
          </Card.Body>
        </Card.Root>
      ) : status === 'loading' || status === 'error' ? null : (
        <Alert.Root status="info" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              No provider data yet. Wait for the search to finish or
              resolve any errors above.
            </Alert.Title>
          </Alert.Content>
        </Alert.Root>
      )}

      {status === 'loading' ? (
        <HStack
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.gray"
          bg="background.primary"
          borderRadius="md"
          px={4}
          py={3}
          css={{
            borderColor: 'var(--tcm-colors-border-gray)',
            backgroundColor: 'var(--tcm-colors-background-primary)',
          }}
        >
          <Box
            w="1rem"
            h="1rem"
            borderRadius="full"
            borderWidth="2px"
            borderStyle="solid"
            borderColor="var(--tcm-colors-border-gray)"
            borderTopColor="var(--tcm-colors-brand-500)"
            css={{ animation: 'tcm-spin 0.8s linear infinite' }}
            aria-label="Loading providers"
          />
          <Text color="text.secondary">
            Searching for providers...
          </Text>
        </HStack>
      ) : null}

      {status === 'error' ? (
        <Alert.Root status="error" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{errorMessage}</Alert.Title>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {status === 'success' || status === 'error' ? (
        <Flex
          justify="space-between"
          align="center"
          gap={4}
          wrap="wrap"
        >
          <button
            onClick={
              showChangeFlow && !shouldStartInChangeFlow
                ? () => setIsChangeFlow(false)
                : onBack
            }
            style={{
              color: 'var(--tcm-colors-text-secondary)',
              textDecoration: 'underline',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <FiArrowLeft size={14} aria-hidden />
            Go back
          </button>
          <StartNewEncounterButton onClick={onStartOver} />
        </Flex>
      ) : null}
    </Stack>
  );
}

type ProcessingChecklistItem = {
  id: string;
  label: string;
  completed: boolean;
  inProgress: boolean;
};

const INITIAL_PROCESSING_ITEMS: ProcessingChecklistItem[] = [
  { id: 'pull-provider-data', label: 'Pulling provider data', completed: false, inProgress: false },
  { id: 'pull-available-appointments', label: 'Pulling available appointments', completed: false, inProgress: false },
  { id: 'final-checks', label: 'Final checks', completed: false, inProgress: false },
];

type ReviewProcessingStepProps = {
  items: ProcessingChecklistItem[];
  isProcessing: boolean;
  providers: ProviderOption[];
  schedulingProvider: ProviderOption | undefined;
  pcpProvider: ProviderOption | undefined;
  pcpPractice: Practice | undefined;
  selectedProviderId: string;
  onProviderIdChange: (id: string) => void;
  selectedSlotId: string;
  onSlotIdChange: (id: string) => void;
  appointmentNotes: string;
  onAppointmentNotesChange: (notes: string) => void;
  onBack: () => void;
  onContinue: () => void;
  onStartOver: () => void;
  /** When true (patient not confirmed in step 2), show Contact CTA instead of Schedule Appointment. */
  contactMode: boolean;
  pacPhone: string | null;
  maxNotesLength?: number;
};

function ReviewProcessingStep({
  items,
  isProcessing,
  providers,
  schedulingProvider,
  pcpProvider,
  pcpPractice,
  selectedProviderId,
  onProviderIdChange,
  selectedSlotId,
  onSlotIdChange,
  appointmentNotes,
  onAppointmentNotesChange,
  onBack,
  onContinue,
  onStartOver,
  contactMode,
  pacPhone,
  maxNotesLength = 500,
}: ReviewProcessingStepProps) {
  const slotsInWindow = React.useMemo(
    () => schedulingProvider?.availableSlotsInWindow ?? [],
    [schedulingProvider?.availableSlotsInWindow],
  );
  const slots = schedulingProvider
    ? collectSlotsForProvider(schedulingProvider)
    : [];
  const sortedSlots = sortSlotsChronologically(slots);
  const hasSlotsInWindow = slotsInWindow.length > 0;
  const mustPickSlotForScheduling =
    slots.length > 0 && !selectedSlotId;
  const hasNoSlotsForScheduling = slots.length === 0;

  // Auto-select first available slot in window on load/provider change
  React.useEffect(() => {
    if (
      !contactMode &&
      hasSlotsInWindow &&
      slotsInWindow.length > 0 &&
      (!selectedSlotId ||
        !slotsInWindow.some((s) => s.id === selectedSlotId))
    ) {
      onSlotIdChange(slotsInWindow[0].id);
    }
  }, [
    schedulingProvider?.id,
    hasSlotsInWindow,
    slotsInWindow,
    contactMode,
    selectedSlotId,
    onSlotIdChange,
  ]);

  const nextSlot =
    schedulingProvider && selectedSlotId
      ? (slots.find((s) => s.id === selectedSlotId) ?? null)
      : null;

  const notesError = validateAppointmentNotes(appointmentNotes, maxNotesLength);

  if (isProcessing) {
    return (
      <Stack gap={6}>
        <Card.Root
          bg="background.primary"
          borderRadius="lg"
          border="1px solid"
          borderColor="border.gray"
          boxShadow="sm"
        >
          <Card.Body p={{ base: 5, md: 6 }}>
            <Stack gap={4}>
              <HStack gap={2}>
                <VisibleInlineSpinner size="1rem" />
                <Stack gap={0}>
                  <Text
                    color="text.brandLight"
                    fontWeight="700"
                    fontSize="lg"
                  >
                    Pulling Patient &amp; Provider Data
                  </Text>
                  <Text color="text.secondary" fontSize="sm">
                    We&apos;re pulling provider data and checking the patient record to determine the patient&apos;s PCP.
                  </Text>
                </Stack>
              </HStack>
              <Stack gap={3}>
                {items.map((item) => (
                  <HStack key={item.id} gap={3}>
                    {item.completed ? (
                      <Circle size="1.2rem" bg="green.500">
                        <Text color="white" fontSize="xs" fontWeight="700">
                          ✓
                        </Text>
                      </Circle>
                    ) : item.inProgress ? (
                      <VisibleInlineSpinner size="1rem" />
                    ) : (
                      <Circle
                        size="1.2rem"
                        border="1px solid"
                        borderColor="border.gray"
                      />
                    )}
                    <Text
                      color={
                        item.completed
                          ? 'green.700'
                          : item.inProgress
                            ? 'text.brandLight'
                            : 'text.secondary'
                      }
                      fontWeight={item.inProgress ? '700' : '500'}
                    >
                      {item.label}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  if (!pcpProvider || !schedulingProvider) {
    return (
      <Stack gap={6}>
        <Alert.Root status="warning" borderRadius="lg">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              No provider is selected. Go back and choose a provider
              returned from the server.
            </Alert.Title>
          </Alert.Content>
        </Alert.Root>
        <Flex
          justify="space-between"
          align="center"
          gap={4}
          wrap="wrap"
        >
          <Button
            variant="outline"
            css={outlineButtonCss}
            onClick={onBack}
          >
            Go Back
          </Button>
          <StartNewEncounterButton onClick={onStartOver} />
        </Flex>
      </Stack>
    );
  }

  const noPcpOnFile = !providers.some((p) => Boolean(p.isPatientPcp));
  const usePacPhone = contactMode || noPcpOnFile;
  // PAC phone only applies when patient is unverified/no PCP on file AND provider is employed.
  const showPacPhone = usePacPhone && schedulingProvider.isEmployed;
  const clinicPhone = pcpProvider.phone?.trim() || pcpPractice?.backline?.trim() || pcpPractice?.phone?.trim() || '';
  const contactPhoneValue = showPacPhone ? (pacPhone?.trim() ?? '') : clinicPhone;
  const hasContactPhone = contactPhoneValue.length > 0;
  const standingOrderText = pcpProvider.standingOrder?.trim() ?? '';
  const hasCustomStandingOrder = standingOrderText.length > 0;
  const fallbackStandingOrderText =
    'No specific standing orders provided. Proceed with standard hospital scheduling protocols.';
  const standingOrderInstruction = (() => {
    if (contactMode) {
      return {
        mode: 'alert' as const,
        status: 'warning' as const,
        title: 'Scheduling guidance',
        text: 'Patient identity unconfirmed. Online scheduling is disabled. Please call the PAC directly to verify the patient and schedule.',
      };
    }

    if (!schedulingProvider.isEmployed) {
      return {
        mode: 'alert' as const,
        status: 'warning' as const,
        title: 'Provider not employed',
        text: 'This is an external provider. Please call to schedule with them.',
      };
    }

    if (hasNoSlotsForScheduling) {
      if (hasCustomStandingOrder) {
        return {
          mode: 'alert' as const,
          status: 'warning' as const,
          title: 'No online appointments available',
          text:
            'Use the custom scheduling instructions below, or call the practice to squeeze the patient in:\n\n' +
            standingOrderText,
        };
      }

      return {
        mode: 'alert' as const,
        status: 'warning' as const,
        title: 'No online appointments available',
        text: 'Provider has no online appointments available. Please call the practice.',
      };
    }

    if (hasCustomStandingOrder) {
      return {
        mode: 'alert' as const,
        status: 'info' as const,
        title: 'Provider instructions',
        text: standingOrderText,
      };
    }

    return {
      mode: 'text' as const,
      text: fallbackStandingOrderText,
    };
  })();
  const slotOptions: SlotSelectOption[] = sortedSlots.map((slot) => {
    const inWin = (
      schedulingProvider.availableSlotsInWindow ?? []
    ).some((s) => s.id === slot.id);
    return {
      value: slot.id,
      label: `${formatProviderSlotTime(slot)}${inWin ? ' — in window' : ' — outside window'}`,
    };
  });
  const selectedSlotOption =
    slotOptions.find((option) => option.value === selectedSlotId) ??
    null;

  return (
    <Stack gap={6}>
      <HStack
        align="center"
        gap={3}
        flexWrap="wrap"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="green.200"
        bg="green.50"
        px={4}
        py={3}
      >
        <Circle size="1.35rem" bg="green.500">
          <Text color="white" fontSize="xs" fontWeight="700">
            ✓
          </Text>
        </Circle>
        <Text fontWeight="700" color="green.900" fontSize="sm">
          Data pull complete
        </Text>
      </HStack>

      <Stack gap={4}>
          <Card.Root
            bg="background.primary"
            borderRadius="lg"
            border="1px solid"
            borderColor="border.gray"
            boxShadow="sm"
          >
            <Card.Body p={{ base: 5, md: 6 }}>
              <Stack gap={6}>
                <Stack gap={1}>
                  <Text
                    color="text.brandDark"
                    fontSize="xl"
                    fontWeight="700"
                  >
                    {contactMode
                      ? 'Contact & follow-up'
                      : 'Follow-up scheduling'}
                  </Text>
                  <Text color="text.secondary" fontSize="sm">
                    {contactMode
                      ? 'Confirm the selected PCP and how you&apos;ll reach the practice.'
                      : 'Review the selected PCP, pick who to schedule with, then choose a time.'}
                  </Text>
                </Stack>

                <Card.Root
                  bg="background.surfaceTertiary"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="border.subtle"
                >
                  <Card.Body>
                    <Stack gap={5}>
                      <Flex
                        justify="space-between"
                        align={{ base: 'flex-start', md: 'center' }}
                        gap={4}
                        wrap="wrap"
                      >
                        <Stack gap={2}>
                          <Text
                            color="text.brandDark"
                            fontWeight="700"
                            fontSize="lg"
                          >
                            {pcpProvider.name}
                          </Text>
                          <HStack
                            gap={4}
                            flexWrap="wrap"
                            fontSize="sm"
                            color="text.secondary"
                          >
                            <HStack gap={1.5}>
                              <Box
                                color="text.brandLight"
                                display="inline-flex"
                                flexShrink={0}
                              >
                                <FiBriefcase size={16} aria-hidden />
                              </Box>
                              <Text>{pcpProvider.specialty}</Text>
                            </HStack>
                            {pcpProvider.isEmployed ? (
                              <Badge
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                bg="green.100"
                                color="green.800"
                                fontSize="xs"
                              >
                                Employed provider
                              </Badge>
                            ) : null}
                            <HStack gap={1.5}>
                              <Box
                                color="text.brandLight"
                                display="inline-flex"
                                flexShrink={0}
                              >
                                <FiHome size={16} aria-hidden />
                              </Box>
                              <Text>{pcpProvider.department}</Text>
                            </HStack>
                          </HStack>
                        </Stack>
                      </Flex>

                      <Stack gap={1}>
                        <Text
                          color="text.secondary"
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          fontWeight="700"
                        >
                          Practice
                        </Text>
                        <PracticeInfoPanel
                          practice={pcpPractice}
                          compact
                          hideLabel
                          useBackline
                        />
                      </Stack>

                      <Box
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="border.subtle"
                        bg="background.surfaceTertiary"
                        p={4}
                      >
                        <Stack gap={3.5}>
                          <Text
                            color="text.secondary"
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            fontWeight="700"
                          >
                            Standing order
                          </Text>
                          {standingOrderInstruction.mode ===
                          'alert' ? (
                            <Alert.Root
                              status={standingOrderInstruction.status}
                              variant="subtle"
                              borderRadius="md"
                            >
                              <Alert.Indicator />
                              <Alert.Content>
                                <Stack gap={1}>
                                  <Alert.Title fontSize="sm">
                                    {standingOrderInstruction.title}
                                  </Alert.Title>
                                  <Alert.Description
                                    fontSize="sm"
                                    color="text.secondary"
                                    whiteSpace="pre-wrap"
                                  >
                                    {standingOrderInstruction.text}
                                  </Alert.Description>
                                </Stack>
                              </Alert.Content>
                            </Alert.Root>
                          ) : (
                            <HStack
                              gap={2}
                              align="flex-start"
                              color="text.secondary"
                            >
                              <Box
                                display="inline-flex"
                                flexShrink={0}
                                mt={0.5}
                              >
                                <FiInfo size={14} aria-hidden />
                              </Box>
                              <Text fontSize="sm">
                                {standingOrderInstruction.text}
                              </Text>
                            </HStack>
                          )}

                          <HStack
                            gap={2}
                            align="flex-start"
                            color="text.secondary"
                          >
                            <Box
                              color="text.brandLight"
                              display="inline-flex"
                              flexShrink={0}
                              mt={0.5}
                            >
                              <FiPhone size={16} aria-hidden />
                            </Box>
                            {hasContactPhone ? (
                              <Text fontSize="sm">
                                {showPacPhone
                                  ? 'Contact PAC: '
                                  : 'Contact clinic: '}
                                <a
                                  href={phoneHref(contactPhoneValue)}
                                  style={{
                                    color:
                                      'var(--tcm-colors-text-brandLight)',
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatPhoneForDisplay(contactPhoneValue)}
                                </a>
                              </Text>
                            ) : (
                              <Text fontSize="sm" fontStyle="italic">
                                No contact number on file. Use
                                standard hospital escalation workflow
                                to reach the{' '}
                                {showPacPhone ? 'PAC' : 'practice'}.
                              </Text>
                            )}
                          </HStack>

                          {schedulingProvider.isEmployed ? (
                            <HStack
                              gap={2}
                              align="flex-start"
                              color="text.secondary"
                            >
                              <Box
                                color="text.brandLight"
                                display="inline-flex"
                                flexShrink={0}
                                mt={0.5}
                              >
                                <FiExternalLink size={16} aria-hidden />
                              </Box>
                              <Text fontSize="sm">
                                <a
                                  href={AFTER_HOURS_FORM_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color:
                                      'var(--tcm-colors-text-brandLight)',
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                  }}
                                >
                                  After-hours formstack
                                </a>
                              </Text>
                            </HStack>
                          ) : null}
                        </Stack>
                      </Box>

                      <Box
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="border.subtle"
                        bg="background.primary"
                        p={{ base: 4, md: 5 }}
                      >
                        <Stack gap={3.5}>
                          <Stack gap={1}>
                            <Text
                              color="text.brandDark"
                              fontWeight="700"
                              fontSize="sm"
                            >
                              Choose scheduling provider
                            </Text>
                            <Text
                              color="text.secondary"
                              fontSize="sm"
                            >
                              Selected PCP is {pcpProvider.name}. Open
                              the list to pick who to schedule
                              with—details and slot summary are in the
                              list.
                            </Text>
                          </Stack>
                          <ProviderChoiceList
                            providers={providers}
                            selectedProviderId={selectedProviderId}
                            onChange={(id) => {
                              onProviderIdChange(id);
                              onSlotIdChange('');
                            }}
                            showSlotsSummary
                            compactTrigger
                          />
                          <Box>
                            <Text
                              color="text.secondary"
                              fontSize="sm"
                              fontWeight="600"
                            >
                              Hospital follow-up appointment type
                            </Text>
                            <Text
                              color="text.brandDark"
                              fontSize="sm"
                              fontWeight="700"
                              mt={1}
                            >
                              {displayOrNone(
                                schedulingProvider.hospitalFollowUpAppointmentType,
                              )}
                            </Text>
                          </Box>
                        </Stack>
                      </Box>

                      {schedulingProvider.isEmployed ? (
                      <Stack gap={2.5}>
                        <HStack gap={6} wrap="wrap" align="center">
                          <HStack gap={2} align="center">
                            <Circle
                              size="1.1rem"
                              bg={
                                hasSlotsInWindow
                                  ? 'green.500'
                                  : 'orange.400'
                              }
                              flexShrink={0}
                            >
                              <Text
                                color="white"
                                fontSize="xs"
                                fontWeight="700"
                              >
                                {hasSlotsInWindow ? '✓' : '!'}
                              </Text>
                            </Circle>
                            <Text
                              color={
                                hasSlotsInWindow
                                  ? 'green.700'
                                  : 'orange.800'
                              }
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {hasSlotsInWindow
                                ? 'Slots available in follow-up window.'
                                : 'No slots available in follow-up window.'}
                            </Text>
                          </HStack>

                          <HStack gap={2} align="center">
                            <Circle
                              size="1.1rem"
                              bg="orange.400"
                              flexShrink={0}
                            >
                              <Text
                                color="white"
                                fontSize="xs"
                                fontWeight="700"
                              >
                                !
                              </Text>
                            </Circle>
                            <Text color="orange.800" fontSize="sm">
                              Follow-up window: 1-14 days.
                            </Text>
                          </HStack>
                        </HStack>

                        {/* Only show next available slot outside window if NO slots in window */}
                        {!hasSlotsInWindow &&
                        schedulingProvider.firstAvailableSlotOutsideWindow ? (
                          <HStack gap={2} align="center">
                            <Circle
                              size="1.1rem"
                              bg="blue.500"
                              flexShrink={0}
                            >
                              <Text
                                color="white"
                                fontSize="xs"
                                fontWeight="700"
                              >
                                i
                              </Text>
                            </Circle>
                            <Text color="blue.800" fontSize="sm">
                              Next available slot:{' '}
                              <Text as="span" fontWeight="700">
                                {formatProviderSlotTime(
                                  schedulingProvider.firstAvailableSlotOutsideWindow,
                                )}
                              </Text>
                            </Text>
                          </HStack>
                        ) : null}

                        {nextSlot ? (
                          <HStack gap={2} align="center">
                            <Circle
                              size="1.1rem"
                              bg="brand.500"
                              flexShrink={0}
                            >
                              <Text
                                color="white"
                                fontSize="xs"
                                fontWeight="700"
                              >
                                ✓
                              </Text>
                            </Circle>
                            <HStack gap={2} flexWrap="wrap">
                              <Box
                                color="text.brandLight"
                                display="inline-flex"
                              >
                                <FiClock size={16} aria-hidden />
                              </Box>
                              <Text
                                color="text.brandDark"
                                fontSize="sm"
                                fontWeight="600"
                              >
                                {formatProviderSlotTime(nextSlot)}
                              </Text>
                              {slotFollowUpWindowLabel(
                                schedulingProvider,
                                nextSlot,
                              ) ? (
                                <Badge
                                  px={2.5}
                                  py={1}
                                  borderRadius="md"
                                  bg="blue.50"
                                  color="text.brandLight"
                                  fontSize="xs"
                                >
                                  {slotFollowUpWindowLabel(
                                    schedulingProvider,
                                    nextSlot,
                                  )}
                                </Badge>
                              ) : null}
                            </HStack>
                          </HStack>
                        ) : mustPickSlotForScheduling ? (
                          <HStack gap={2} align="center">
                            <Circle
                              size="1.1rem"
                              bg="blue.400"
                              flexShrink={0}
                            >
                              <Text
                                color="white"
                                fontSize="xs"
                                fontWeight="700"
                              >
                                ?
                              </Text>
                            </Circle>
                            <Text
                              color="blue.800"
                              fontSize="sm"
                              fontWeight="600"
                            >
                              Slots are available. Select an
                              appointment time to continue.
                            </Text>
                          </HStack>
                        ) : null}
                      </Stack>
                      ) : null}

                      {(!contactMode && hasNoSlotsForScheduling) ||
                      !schedulingProvider.isEmployed ? null : (
                        <Field.Root required={!contactMode}>
                          <Field.Label>
                            <HStack gap={2} align="center">
                              <Box
                                color="text.brandLight"
                                display="inline-flex"
                              >
                                <FiClock size={16} aria-hidden />
                              </Box>
                              <Text as="span">Appointment time</Text>
                              {!contactMode ? (
                                <Field.RequiredIndicator />
                              ) : null}
                            </HStack>
                          </Field.Label>
                          {contactMode ? (
                            <Text
                              color="text.secondary"
                              fontSize="xs"
                              mb={1}
                            >
                              Scheduling is disabled for this
                              encounter. Use standing order and
                              contact details.
                            </Text>
                          ) : null}
                          <Select<SlotSelectOption, false>
                            classNamePrefix="tcm-slot-select"
                            isDisabled={contactMode}
                            isSearchable
                            options={slotOptions}
                            value={selectedSlotOption}
                            placeholder={
                              contactMode
                                ? 'Scheduling disabled for this encounter'
                                : 'Select time…'
                            }
                            onChange={(option) =>
                              onSlotIdChange(option?.value ?? '')
                            }
                            noOptionsMessage={() =>
                              'No appointment times available'
                            }
                            styles={{
                              container: (base) => ({
                                ...base,
                                width: '100%',
                                minWidth: '20rem',
                                maxWidth: '32rem',
                              }),
                              control: (base, state) => ({
                                ...base,
                                minHeight: '2.75rem',
                                borderRadius: '0.375rem',
                                borderWidth: '1px',
                                borderColor: state.isFocused
                                  ? 'var(--tcm-colors-brand-500)'
                                  : 'var(--tcm-colors-border-gray)',
                                backgroundColor:
                                  'var(--tcm-colors-background-primary)',
                                boxShadow: state.isFocused
                                  ? '0 0 0 1px var(--tcm-colors-brand-500)'
                                  : 'none',
                                cursor: contactMode
                                  ? 'not-allowed'
                                  : 'pointer',
                                '&:hover': {
                                  borderColor: state.isFocused
                                    ? 'var(--tcm-colors-brand-500)'
                                    : 'var(--tcm-colors-border-gray)',
                                },
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                paddingLeft: '0.5rem',
                                paddingRight: '0.5rem',
                              }),
                              input: (base) => ({
                                ...base,
                                color:
                                  'var(--tcm-colors-text-primary)',
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color:
                                  'var(--tcm-colors-text-secondary)',
                                fontSize: '0.95rem',
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color:
                                  'var(--tcm-colors-text-primary)',
                                fontSize: '0.95rem',
                              }),
                              menu: (base) => ({
                                ...base,
                                border:
                                  '1px solid var(--tcm-colors-border-gray)',
                                boxShadow:
                                  '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                borderRadius: '0.5rem',
                                overflow: 'hidden',
                                zIndex: 20,
                              }),
                              option: (base, state) => ({
                                ...base,
                                fontSize: '0.9rem',
                                backgroundColor: state.isSelected
                                  ? 'var(--tcm-colors-brand-50)'
                                  : state.isFocused
                                    ? 'var(--tcm-colors-background-surfaceTertiary)'
                                    : 'var(--tcm-colors-background-primary)',
                                color:
                                  'var(--tcm-colors-text-primary)',
                                cursor: 'pointer',
                              }),
                            }}
                          />
                        </Field.Root>
                      )}

                      <Field.Root invalid={!!notesError}>
                        <Field.Label>
                          <HStack gap={2} align="center">
                            <Box
                              color="text.brandLight"
                              display="inline-flex"
                            >
                              <FiClipboard size={16} aria-hidden />
                            </Box>
                            <Text as="span">Appointment notes</Text>
                          </HStack>
                        </Field.Label>
                        <Textarea
                          placeholder="Add any notes about this appointment…"
                          value={appointmentNotes}
                          onChange={(e) =>
                            onAppointmentNotesChange(e.target.value)
                          }
                          rows={5}
                          bg="background.primary"
                          border="1px solid"
                          borderColor="border.gray"
                          px={3}
                          py={2}
                        />
                        {notesError && (
                          <Field.ErrorText>{notesError}</Field.ErrorText>
                        )}
                      </Field.Root>
                    </Stack>
                  </Card.Body>
                </Card.Root>

                <Box
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="border.subtle"
                  bg="background.surfaceTertiary"
                  px={{ base: 4, md: 5 }}
                  py={4}
                >
                  <Flex
                    justify="space-between"
                    align={{ base: 'stretch', md: 'flex-end' }}
                    gap={5}
                    direction={{ base: 'column', md: 'row' }}
                    wrap="wrap"
                  >
                    <Stack
                      gap={1}
                      flex="1"
                      minW={0}
                      aria-live="polite"
                    >
                      <Text
                        color="text.secondary"
                        fontSize="xs"
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        Next step
                      </Text>
                      <Text
                        color="text.brandDark"
                        fontWeight="700"
                        fontSize="md"
                      >
                        {contactMode || hasNoSlotsForScheduling
                          ? 'Resolve manually'
                          : 'Schedule appointment'}
                      </Text>
                    </Stack>
                    <HStack
                      gap={3}
                      flexWrap="wrap"
                      justify={{ base: 'stretch', md: 'flex-end' }}
                    >
                      <Button
                        variant="outline"
                        css={outlineButtonCss}
                        onClick={onBack}
                      >
                        Go Back
                      </Button>
                      <StartNewEncounterButton onClick={onStartOver} />
                      {!contactMode && !hasNoSlotsForScheduling && (
                        <Button
                          colorPalette="brand"
                          variant="solid"
                          css={{
                            bg: 'background.brandLight',
                            color: 'white',
                            px: 6,
                            minH: 10,
                            borderRadius: 'md',
                            fontWeight: 'bold',
                          }}
                          onClick={onContinue}
                          disabled={!nextSlot || !!notesError}
                        >
                          Schedule Appointment
                        </Button>
                      )}
                    </HStack>
                  </Flex>
                </Box>
              </Stack>
            </Card.Body>
          </Card.Root>
        </Stack>
    </Stack>
  );
}

type WizardActionBarProps = {
  currentStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  onBack: () => void;
  onNext: () => void;
  onStartOver: () => void;
  startOverDisabled?: boolean;
  hideBack?: boolean;
};

function WizardActionBar({
  currentStep,
  isFirstStep,
  isLastStep,
  onBack,
  onNext,
  onStartOver,
  startOverDisabled = false,
  hideBack = false,
}: WizardActionBarProps) {
  return (
    <Flex justify="space-between" align="center" gap={4} wrap="wrap">
      <HStack>
        {!isFirstStep ? (
          <StartNewEncounterButton onClick={onStartOver} disabled={startOverDisabled} />
        ) : null}
      </HStack>
      <HStack>
        {!isFirstStep && !hideBack ? (
          <Button
            variant="outline"
            css={outlineButtonCss}
            onClick={onBack}
          >
            Back
          </Button>
        ) : null}
        {!isLastStep ? (
          <Button
            colorPalette="brand"
            variant="solid"
            css={{
              bg: 'background.brandLight',
              color: 'white',
              px: 6,
              minH: 10,
              borderRadius: 'md',
              fontWeight: 'bold',
            }}
            onClick={onNext}
          >
            {currentStep === 1
              ? 'Continue to Patient Check'
              : 'Continue'}
          </Button>
        ) : null}
      </HStack>
    </Flex>
  );
}

export function HomePage({
  embedded = false,
  directEntry,
}: {
  embedded?: boolean;
  directEntry?: { tablespace: string; athenaPatientId: string; dischargeDate: string };
}) {
  const apiClient = useApiClient();
  const {
    selectedHospitalId,
    setSelectedHospitalId,
    hospitals,
    hospitalsLoading,
  } = useHospitalContext();
  const { sessionId, resetSession } = useEncounterSession();
  const [currentStep, setCurrentStep] = useState(1);
  /** True when step 2 used "No, but Search for Providers Anyway" (patient not confirmed). */
  const [skippedPatientVerification, setSkippedPatientVerification] =
    useState(false);
  /**
   * Ref mirrors `skippedPatientVerification` but is set synchronously in step 2 handlers
   * before navigation. Async provider-search mapping reads this so `isPatientPcp` is never
   * shown after "search anyway" even if state timing is off.
   */
  const skippedPatientVerificationRef = useRef(false);
  const directEntryKeyRef = useRef<string | null>(null);
  const providerSearchDoneRef = useRef(false);
  const providerSearchCompleteRef = useRef<(() => void) | null>(null);
  const [directLookupStatus, setDirectLookupStatus] = useState<
    'idle' | 'loading' | 'error'
  >('idle');
  const [directLookupError, setDirectLookupError] = useState('');
  const todayDate = useMemo(() => getTodayDateInputValue(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lookupResults, setLookupResults] = useState<
    PatientLookupResult[]
  >([]);
  const [submittedValues, setSubmittedValues] =
    useState<PatientLookupFormValues | null>(null);
  const [athenaPatient, setAthenaPatient] =
    useState<AthenaPatient | null>(null);
  const [scheduleState, setScheduleState] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [scheduleResult, setScheduleResult] =
    useState<GenericResponse | null>(null);
  const [scheduleError, setScheduleError] = useState<string>('');
  const [isReviewProcessing, setIsReviewProcessing] = useState(false);
  const [processingItems, setProcessingItems] = useState<ProcessingChecklistItem[]>(INITIAL_PROCESSING_ITEMS);
  const [formValues, setFormValues] =
    useState<PatientLookupFormValues>({
      patientName: '',
      dateOfBirth: '',
      estimatedDischargeDate: todayDate,
    });

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        label: 'Patient Lookup',
        description: 'Enter patient information to begin',
      },
      {
        label: 'Patient Check',
        description: 'Verify patient information',
      },
      {
        label: 'Provider Search',
        description: 'Find and confirm the patient’s PCP',
      },
      {
        label: 'Choose Appointment',
        description:
          'Confirm provider and choose an appointment time',
      },
      {
        label: 'Summary',
        description: 'Scheduled appointment confirmation',
      },
    ],
    [],
  );

  useEffect(() => {
    if (currentStep < 1 || currentStep > steps.length) return;
    analytics.trackEvent('step', 'followup', {
      step: steps[currentStep - 1]?.label,
      stepIndex: currentStep,
    });
  }, [currentStep, steps]);

  const [providerSearchResults, setProviderSearchResults] = useState<
    ProviderOption[]
  >([]);

  const [providerSearchPractices, setProviderSearchPractices] =
    useState<Practice[]>([]);

  const [providerSearchStatus, setProviderSearchStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [providerSearchErrorMessage, setProviderSearchErrorMessage] =
    useState<string>('');
  const [providerSearchPacPhone, setProviderSearchPacPhone] =
    useState<string | null>(null);
  const [alreadyScheduled, setAlreadyScheduled] =
    useState<AlreadyScheduled | null>(null);
  const [isStartOverModalOpen, setIsStartOverModalOpen] =
    useState(false);
  const [isWrapCodeModalOpen, setIsWrapCodeModalOpen] = useState(false);

  const [isNewPatientFromSearch, setIsNewPatientFromSearch] = useState<
    boolean | null
  >(null);

  /** PCP-of-record for this encounter, chosen in step 3. */
  const [pcpProviderId, setPcpProviderId] = useState<string | null>(
    null,
  );
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');

  const isNewPatient = skippedPatientVerification ? true : (isNewPatientFromSearch ?? false);

  useEffect(() => {
    if (currentStep !== 4) return;
    const selectedHospital = hospitals.find(
      (h) => h.id === selectedHospitalId,
    );
    const resolvedPcpProvider = resolveWizardProvider(providerSearchResults, pcpProviderId ?? '');
    const isConfirmedPcpEstablished = providerSearchResults.some(
      (p) => p.isPatientPcp && p.id === resolvedPcpProvider?.id,
    );
    const isFirstTimePcp = !isNewPatient && !isConfirmedPcpEstablished && !!pcpProviderId;
    setAppointmentNotes(
      buildAppointmentNotes({
        isNewPatient,
        hospitalName: selectedHospital?.name,
        estimatedDischargeDate: submittedValues?.estimatedDischargeDate,
        insuranceName: athenaPatient?.insurance_provider,
        isFirstTimePcp,
        pcpName: resolvedPcpProvider?.name,
      }),
    );
  }, [
    currentStep,
    isNewPatient,
    hospitals,
    selectedHospitalId,
    submittedValues,
    athenaPatient,
    providerSearchResults,
    pcpProviderId,
  ]);

  const {
    tablespace: directTablespace,
    athenaPatientId: directAthenaPatientId,
    dischargeDate: directDischargeDate,
  } = directEntry ?? {}

  useEffect(() => {
    if (!directTablespace || !directAthenaPatientId) {
      return;
    }

    const directEntryKey = `${directTablespace}:${directAthenaPatientId}`;
    if (directEntryKeyRef.current === directEntryKey) {
      return;
    }

    directEntryKeyRef.current = directEntryKey;
    setDirectLookupStatus('loading');

    void fetchDirectPatient(apiClient, {
      tablespace: directTablespace,
      athenaPatientId: directAthenaPatientId,
    }).then((patient) => {
      const values: PatientLookupFormValues = {
        patientName: patient.full_name,
        dateOfBirth: patient.date_of_birth,
        estimatedDischargeDate: directDischargeDate ?? '',
      };
      setAthenaPatient(patient);
      setSelectedHospitalId(patient.hospital_id);
      setFormValues(values);
      setSubmittedValues(values);
      setLookupResults([
        {
          patientId: patient.patient_id ?? 'pt-direct',
          patientName: patient.full_name,
          dateOfBirth: patient.date_of_birth,
          confidenceLabel: 'Direct lookup',
        },
      ]);
      setSkippedPatientVerification(false);
      skippedPatientVerificationRef.current = false;
      setPcpProviderId(null);
      setDirectLookupStatus('idle');
      setCurrentStep(2);
    }).catch((error: unknown) => {
      const message =
        getAxiosErrorStatus(error) === 404
          ? `Tablespace "${directTablespace}" is not recognized. Please check the URL or use the form to search manually.`
          : getAxiosErrorMessage(error);
      setDirectLookupError(message);
      setDirectLookupStatus('error');
    });

  }, [apiClient, directTablespace, directAthenaPatientId, directDischargeDate, setSelectedHospitalId]);

  const canSubmit =
    !hospitalsLoading &&
    Boolean(selectedHospitalId) &&
    formValues.patientName.trim().length >= 2 &&
    Boolean(formValues.dateOfBirth) &&
    Boolean(formValues.estimatedDischargeDate);

  const handleFieldChange = (
    field: keyof PatientLookupFormValues,
    value: string,
  ) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleLookup = async () => {
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      if (!selectedHospitalId) {
        setErrorMessage('Please select a hospital before searching.');
        return;
      }

      const patient = await findPatient(apiClient, {
        hospitalId: selectedHospitalId,
        patientName: formValues.patientName,
        birthDate: formValues.dateOfBirth,
        sessionId,
      });

      setAthenaPatient(patient);
      setScheduleState('idle');
      setScheduleResult(null);
      setScheduleError('');
      setProviderSearchPacPhone(null);

      const results: PatientLookupResult[] = [
        {
          patientId: patient.patient_id ?? 'pt-athena',
          patientName: patient.full_name,
          dateOfBirth: patient.date_of_birth,
          confidenceLabel: 'Best match',
        },
      ];

      setLookupResults(results);
      setSubmittedValues(formValues);
      setSkippedPatientVerification(false);
      skippedPatientVerificationRef.current = false;
      setPcpProviderId(null);
      setCurrentStep(2);
    } catch (error) {
      setErrorMessage(getAxiosErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Show presets in QA and local development (localhost). */
  const envSignals = [
    import.meta.env.VITE_APP_ENV,
    import.meta.env.VITE_GIT_SHA,
    import.meta.env.VITE_AUTH_APP_ORIGIN,
    typeof window !== 'undefined' ? window.location.hostname : '',
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  const isQaEnvironment = envSignals.some(
    (value) =>
      value === 'qa' ||
      value.includes('-qa') ||
      value.includes('qa-') ||
      value.includes('qa.'),
  );
  const isLocalDevelopment =
    import.meta.env.MODE === 'development' ||
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'));
  const showPresetPatients = isQaEnvironment || isLocalDevelopment;
  const presetPatients = [
    {
      patientName: 'Gladys Rhodes',
      dateOfBirth: '2001-01-01',
      dateOfBirthLabel: '1/1/2001',
    },
    {
      patientName: 'Adriano Provider',
      dateOfBirth: '1999-09-09',
      dateOfBirthLabel: '9/9/1999',
    },
    {
      patientName: 'Madawc Aintemployed',
      dateOfBirth: '1988-08-08',
      dateOfBirthLabel: '8/8/1988',
    },
  ] as const;

  const handlePresetSelect = (
    patientName: string,
    dateOfBirth: string,
  ) => {
    setFormValues((current) => ({
      ...current,
      patientName,
      dateOfBirth,
    }));
    setErrorMessage('');
  };

  // Preserve backend order (backend sorts, e.g. accepting new patients first).
  const effectiveProviderOptions = providerSearchResults;

  /** Step 3 PCP selection: hide non-accepting providers for new patients. Step 4 shows all slot
   *  providers regardless — cross-scheduling instructions may route to them. */
  const step3ProviderOptions = useMemo(
    () =>
      isNewPatient
        ? effectiveProviderOptions.filter((p) => p.isAcceptingNewPatients !== false)
        : effectiveProviderOptions,
    [effectiveProviderOptions, isNewPatient],
  );

  useEffect(() => {
    const p = resolveWizardProvider(
      effectiveProviderOptions,
      selectedProviderId,
    );
    if (!p) {
      setSelectedSlotId('');
      return;
    }
    const slots = collectSlotsForProvider(p);
    setSelectedSlotId((prev) => {
      if (prev && slots.some((s) => s.id === prev)) return prev;
      return '';
    });
  }, [selectedProviderId, effectiveProviderOptions]);

  useEffect(() => {
    if (currentStep !== 3) {
      return;
    }
    if (!submittedValues || !selectedHospitalId) {
      return;
    }

    let isCancelled = false;

    const runProviderSearch = async () => {
      providerSearchDoneRef.current = false;
      try {
        setProviderSearchStatus('loading');
        setProviderSearchErrorMessage('');
        setAlreadyScheduled(null);

        const { patientId, tableSpace } =
          trustedAthenaContextForProviderSearch(
            athenaPatient,
            skippedPatientVerification,
          );

        const response = await providerSearch(apiClient, {
          hospitalId: selectedHospitalId,
          patientName: submittedValues.patientName,
          patientId,
          tableSpace,
          birthDate: submittedValues.dateOfBirth,
          expectedDischargeDate:
            submittedValues.estimatedDischargeDate,
          sessionId,
        });

        if (isCancelled) return;

        setProviderSearchPractices(response.practices);
        setProviderSearchPacPhone(response.pac_phone ?? null);
        setAlreadyScheduled(response.already_scheduled ?? null);
        setIsNewPatientFromSearch(resolveIsNewPatientFromSearch(response));

        const mappedProviders: ProviderOption[] =
          response.providers.map((provider) => {
            const matchedPractice =
              (provider.practice_id
                ? response.practices.find(
                    (p) => p.id === provider.practice_id,
                  )
                : undefined) ??
              (response.practices.length === 1
                ? response.practices[0]
                : undefined);

            const hideEstablishedPcp =
              skippedPatientVerificationRef.current;
            return {
              id: provider.id,
              externalId: provider.external_id ?? null,
              name: provider.name,
              nickname: provider.nickname ?? null,
              specialty: 'Primary Care',
              department: matchedPractice?.name ?? 'Practice',
              practiceId: provider.practice_id ?? null,
              practiceRecord: matchedPractice ?? null,
              isEmployed: provider.is_employed,
              phone: provider.phone ?? undefined,
              email: undefined,
              isAcceptingNewPatients:
                provider.accepting_new_patients ?? true,
              tablespace: provider.tablespace ?? null,
              standingOrder: provider.standing_order ?? null,
              availableSlotsInWindow: provider.available_slots_in_window ?? [],
              firstAvailableSlotOutsideWindow: provider.first_available_slot_outside_window ?? null,
              hospitalFollowUpAppointmentType:
                provider.hospital_follow_up_appointment_type ?? null,
              isPatientPcp: hideEstablishedPcp
                ? false
                : (provider.is_patient_pcp ?? false),
              distanceFromPatientMiles:
                provider.distance_from_patient_miles ?? null,
            };
          });

        const isSearchAnyway = skippedPatientVerificationRef.current;

        setProviderSearchResults(mappedProviders);

        setProviderSearchStatus('success');
        if (mappedProviders.length === 0) {
          setProviderSearchStatus('error');
          setProviderSearchErrorMessage(
            'No providers were returned for this patient/hospital.',
          );
          setSelectedProviderId('');
        }
        if (mappedProviders.length > 0) {
          const hasEstablishedPcp = mappedProviders.some((p) =>
            Boolean(p.isPatientPcp),
          );

          if (isSearchAnyway || !hasEstablishedPcp) {
            // Force explicit assignment when we can't assume PCP or backend did not
            // return any established PCP for this patient.
            setSelectedProviderId('');
            setPcpProviderId(null);
          } else {
            const pcpDefault = mappedProviders.find(
              (p) => p.isPatientPcp,
            );
            const defaultId = providerSelectKey(
              pcpDefault ?? mappedProviders[0]!,
            );
            setSelectedProviderId(defaultId);
            setPcpProviderId(defaultId);
          }
        }

        // Scheduling needs to rerun with the newly selected provider/slot.
        setScheduleState('idle');
        setScheduleResult(null);
        setScheduleError('');
        providerSearchDoneRef.current = true;
        providerSearchCompleteRef.current?.();
        providerSearchCompleteRef.current = null;
      } catch (error) {
        if (isCancelled) return;
        console.error('Provider search failed', error);
        setProviderSearchResults([]);
        setProviderSearchPractices([]);
        setProviderSearchPacPhone(null);
        setAlreadyScheduled(null);
        setIsNewPatientFromSearch(null);
        setSelectedProviderId('');
        setProviderSearchStatus('error');
        setProviderSearchErrorMessage(getAxiosErrorMessage(error));
        providerSearchDoneRef.current = true;
        providerSearchCompleteRef.current?.();
        providerSearchCompleteRef.current = null;
      }
    };

    void runProviderSearch();

    return () => {
      isCancelled = true;
    };
  }, [
    apiClient,
    athenaPatient,
    currentStep,
    selectedHospitalId,
    sessionId,
    skippedPatientVerification,
    submittedValues,
  ]);

  const goToNextStep = () => {
    setCurrentStep((current) => Math.min(current + 1, steps.length));
  };

  const goToPreviousStep = () => {
    if (currentStep === steps.length && scheduleState === 'error') {
      setScheduleState('idle');
      setScheduleResult(null);
      setScheduleError('');
    }
    setCurrentStep((current) => Math.max(current - 1, 1));
  };

  const doReset = () => {
    resetSession();
    setIsStartOverModalOpen(false);
    setIsWrapCodeModalOpen(false);
    setCurrentStep(1);
    setSkippedPatientVerification(false);
    skippedPatientVerificationRef.current = false;
    setErrorMessage('');
    setLookupResults([]);
    setSubmittedValues(null);
    setAthenaPatient(null);
    setProviderSearchResults([]);
    setProviderSearchPractices([]);
    setProviderSearchPacPhone(null);
    setAlreadyScheduled(null);
    setIsNewPatientFromSearch(null);
    setProviderSearchStatus('idle');
    setProviderSearchErrorMessage('');
    setPcpProviderId(null);
    setIsReviewProcessing(false);
    setProcessingItems(INITIAL_PROCESSING_ITEMS);
    setScheduleState('idle');
    setScheduleResult(null);
    setScheduleError('');
    setSelectedProviderId('');
    setSelectedSlotId('');
    setFormValues({
      patientName: '',
      dateOfBirth: '',
      estimatedDischargeDate: todayDate,
    });
  };

  const confirmStartOver = () => {
    setIsStartOverModalOpen(false);
    if (!WRAP_CODE_OUTCOME_ENABLED || scheduleState === 'success') {
      doReset();
      return;
    }
    if (alreadyScheduled) {
      postEncounterOutcome(apiClient, {
        hospitalId: selectedHospitalId,
        exitTrigger: 'start_new_encounter',
        reportedAppointmentScheduled: false,
        wrapCode: ALREADY_SCHEDULED_WRAP_CODE,
      }).catch(() => {}).finally(doReset);
      return;
    }
    setIsWrapCodeModalOpen(true);
  };

  const handleWrapCodeSubmit = (scheduledOutside: boolean, wrapCode: string, additionalDetails: string | undefined) => {
    postEncounterOutcome(apiClient, {
      hospitalId: selectedHospitalId,
      exitTrigger: 'start_new_encounter',
      reportedAppointmentScheduled: scheduledOutside,
      wrapCode,
      additionalDetails,
    }).catch(() => {}).finally(doReset);
  };

  const handleStartOver = () => {
    setIsStartOverModalOpen(true);
  };

  useEffect(() => {
    if (currentStep !== 3 || !isReviewProcessing) {
      return;
    }

    let isCancelled = false;

    const markItem = (id: string, patch: { inProgress?: boolean; completed?: boolean }) =>
      setProcessingItems((current) =>
        current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );

    const runProcessing = async () => {
      markItem('pull-provider-data', { inProgress: true });
      await new Promise((resolve) => setTimeout(resolve, 4000));
      if (isCancelled) return;
      markItem('pull-provider-data', { inProgress: false, completed: true });

      markItem('pull-available-appointments', { inProgress: true });
      await new Promise<void>((resolve) => {
        if (providerSearchDoneRef.current) {
          resolve();
        } else {
          providerSearchCompleteRef.current = resolve;
        }
      });
      if (isCancelled) return;
      markItem('pull-available-appointments', { inProgress: false, completed: true });

      markItem('final-checks', { inProgress: true });
      await new Promise((resolve) => setTimeout(resolve, 600));
      if (isCancelled) return;
      markItem('final-checks', { inProgress: false, completed: true });

      setIsReviewProcessing(false);
    };

    void runProcessing();

    return () => {
      isCancelled = true;
      providerSearchCompleteRef.current?.();
      providerSearchCompleteRef.current = null;
    };
  }, [currentStep, isReviewProcessing]);

  const currentStepDefinition = steps[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;
  const hideSummaryBackButton =
    currentStep === 5 &&
    !skippedPatientVerification &&
    scheduleState !== 'error';
  /** Provider used for scheduling in step 4/5 (may differ from PCP-of-record). */
  const selectedProvider: ProviderOption | undefined =
    resolveWizardProvider(
      effectiveProviderOptions,
      selectedProviderId,
    );
  /** PCP-of-record chosen in step 3; remains fixed even if scheduling provider changes. */
  const pcpProvider: ProviderOption | undefined =
    resolveWizardProvider(
      effectiveProviderOptions,
      pcpProviderId ?? '',
    );

  const selectedPractice: Practice | undefined = useMemo(
    () =>
      resolvePracticeForProvider(
        selectedProvider,
        providerSearchPractices,
      ),
    [selectedProvider, providerSearchPractices],
  );
  const pcpPractice: Practice | undefined = useMemo(
    () =>
      resolvePracticeForProvider(
        pcpProvider,
        providerSearchPractices,
      ),
    [pcpProvider, providerSearchPractices],
  );

  const nextSlotForScheduling: AvailableSlot | null = useMemo(() => {
    const p = resolveWizardProvider(
      effectiveProviderOptions,
      selectedProviderId,
    );
    if (!p || !selectedSlotId) return null;
    const slots = collectSlotsForProvider(p);
    return slots.find((s) => s.id === selectedSlotId) ?? null;
  }, [selectedProviderId, selectedSlotId, effectiveProviderOptions]);

  useEffect(() => {
    if (currentStep !== 5) {
      return;
    }

    if (scheduleState !== 'idle') {
      return;
    }

    // PCP-of-record is chosen in step 3 (pcpProviderId); scheduling uses selectedProvider.
    const tablespace = nextSlotForScheduling
      ? resolveScheduleTablespace(
          nextSlotForScheduling,
          selectedProvider,
          athenaPatient,
        )
      : '';

    if (
      !selectedProvider ||
      !athenaPatient ||
      athenaPatient.patient_id == null ||
      String(athenaPatient.patient_id).trim() === '' ||
      !nextSlotForScheduling ||
      !nextSlotForScheduling.id ||
      !tablespace ||
      !selectedProvider.hospitalFollowUpAppointmentType?.trim()
    ) {
      setScheduleState('error');
      const detail = !athenaPatient?.patient_id
        ? 'Missing patient id from lookup.'
        : !nextSlotForScheduling?.id
          ? 'No appointment slot was selected or returned from provider search.'
          : !tablespace
            ? 'Slot is missing tablespace (and no fallback from provider or patient record).'
            : 'Selected provider is missing a hospital follow-up appointment type.';
      setScheduleError(
        `Cannot schedule: ${detail} Confirm patient lookup, provider search, and that a slot is available.`,
      );
      return;
    }

    setScheduleState('loading');
    setScheduleError('');

    const schedulingPhysicianId = selectedProvider.externalId?.trim();
    if (!schedulingPhysicianId) {
      setScheduleState('error');
      setScheduleError(
        'Cannot schedule: selected provider is missing an Athena external_id.',
      );
      return;
    }

    const crossSchedulePrefix =
      !isNewPatient && pcpProvider && selectedProvider?.id !== pcpProvider.id
        ? `This patient is cross-scheduled. Regular provider is ${pcpProvider.name}.\n`
        : '';

    void scheduleAppointment(apiClient, {
      tablespace,
      sessionId,
      body: {
        patient_id: String(athenaPatient.patient_id),
        slot_id: nextSlotForScheduling.id,
        physician_id: schedulingPhysicianId,
        appointment_type:
          selectedProvider.hospitalFollowUpAppointmentType.trim(),
        appointment_notes: crossSchedulePrefix + appointmentNotes,
        hospital_id: selectedHospitalId,
        appointment_time: formatSlotForPrint(nextSlotForScheduling) || null,
        appointment_location: selectedPractice?.name ?? null,
        appointment_provider: selectedProvider.name,
      },
    })
      .then((res) => {
        setScheduleResult(res);
        setScheduleState('success');
        analytics.trackEvent('create', 'followup');
      })
      .catch((error) => {
        console.error('scheduleAppointment failed', error);
        const msg = getAxiosErrorMessage(error);
        const status = getAxiosErrorStatus(error);
        setScheduleError(
          status === 422
            ? `${msg} — Check appointment notes for invalid content (no angle brackets, no control characters, 500 character limit).`
            : msg,
        );
        setScheduleState('error');
      });
  }, [
    apiClient,
    appointmentNotes,
    athenaPatient,
    currentStep,
    isNewPatient,
    nextSlotForScheduling,
    pcpProvider,
    scheduleState,
    selectedHospitalId,
    selectedPractice,
    selectedProvider,
    sessionId,
    skippedPatientVerification,
  ]);

  const reviewContactPhone = useMemo(() => {
    const fromPractice = selectedPractice?.backline?.trim() || selectedPractice?.phone?.trim();
    const fromProvider = selectedProvider?.phone?.trim();
    const fromPatient = athenaPatient?.phone?.trim();
    return fromPractice || fromProvider || fromPatient || '';
  }, [
    athenaPatient?.phone,
    selectedPractice?.backline,
    selectedPractice?.phone,
    selectedProvider?.phone,
  ]);

  const patientCheckData: PatientCheckData | null = submittedValues
    ? {
        // Patient Check reflects find_patient only — no form fallbacks for missing API fields.
        fullName: athenaPatient?.full_name ?? '',
        dateOfBirth: athenaPatient?.date_of_birth ?? '',
        phoneNumber: athenaPatient?.phone ?? '',
        address: athenaPatient?.address ?? '',
        insuranceProvider: athenaPatient?.insurance_provider ?? '',
        insuranceId: athenaPatient?.insurance_id ?? '',
        athenaPatientId:
          athenaPatient?.patient_id != null
            ? String(athenaPatient.patient_id)
            : '',
        athenaTableSpace:
          athenaPatient?.athena_tablespace != null
            ? String(athenaPatient.athena_tablespace)
            : '',
        emrLink: athenaPatient?.emr_link ?? null,
        matchFound: athenaPatient?.match_found === true,
      }
    : null;

  const trustedAthenaCtx = trustedAthenaContextForProviderSearch(
    athenaPatient,
    skippedPatientVerification,
  );

  return (
    <Box minH="100vh" bg="background.surfaceTertiary">
      <Helmet>
        <title>Follow-Up Scheduling</title>
      </Helmet>
      {embedded ? null : <NavBar />}
      <Container maxW="7xl" py={{ base: 8, md: 10 }}>
        <Stack gap={6}>
          <Card.Root
            bg="background.primary"
            borderRadius="2xl"
            border="1px solid"
            borderColor="border.subtle"
            boxShadow="sm"
          >
            <Card.Body p={{ base: 5, md: 8 }}>
              <FollowUpStepper
                currentStep={currentStep}
                steps={steps}
                sessionId={sessionId}
                patientName={
                  trustedAthenaCtx.patientId != null
                    ? patientCheckData?.fullName || submittedValues?.patientName
                    : null
                }
                athenaPatientId={trustedAthenaCtx.patientId}
                athenaTableSpace={trustedAthenaCtx.tableSpace}
                emrLink={
                  trustedAthenaCtx.patientId != null
                    ? patientCheckData?.emrLink
                    : undefined
                }
              />
            </Card.Body>
          </Card.Root>

          <Card.Root
            bg="background.primary"
            borderRadius="2xl"
            border="1px solid"
            borderColor="border.subtle"
            boxShadow="sm"
          >
            <Card.Body p={{ base: 5, md: 8 }}>
              {currentStep === 1 ? (
                directLookupStatus === 'loading' ? (
                  <Center minH="280px">
                    <Stack align="center" gap={4}>
                      <VisibleInlineSpinner size="1.35rem" thickness="2.5px" />
                      <Text color="text.secondary" fontSize="sm">
                        Looking up patient…
                      </Text>
                    </Stack>
                  </Center>
                ) : (
                <Stack gap={6}>
                  <Box>
                    <Text
                      color="text.brandDark"
                      fontSize="2xl"
                      fontWeight="700"
                    >
                      Patient Lookup
                    </Text>
                    <Text color="text.secondary" mt={2}>
                      Select a hospital, then enter patient details to
                      fetch the patient record before moving to the
                      next step.
                    </Text>
                  </Box>

                  {(directLookupStatus === 'error' && directLookupError) ? (
                    <Alert.Root status="error" borderRadius="lg">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>{directLookupError}</Alert.Title>
                      </Alert.Content>
                    </Alert.Root>
                  ) : errorMessage ? (
                    <Alert.Root status="error" borderRadius="lg">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>{errorMessage}</Alert.Title>
                      </Alert.Content>
                    </Alert.Root>
                  ) : null}

                  {hospitalsLoading ? (
                    <Center
                      minH="280px"
                      borderRadius="xl"
                      border="1px dashed"
                      borderColor="border.subtle"
                      bg="background.surfaceTertiary"
                    >
                      <Stack align="center" gap={4}>
                        <VisibleInlineSpinner
                          size="1.35rem"
                          thickness="2.5px"
                        />
                        <Text color="text.secondary" fontSize="sm">
                          Loading hospitals…
                        </Text>
                      </Stack>
                    </Center>
                  ) : hospitals.length === 0 ? (
                    <Alert.Root status="warning" borderRadius="lg">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>
                          No hospitals were returned from the server.
                          You cannot continue until at least one
                          hospital is available.
                        </Alert.Title>
                      </Alert.Content>
                    </Alert.Root>
                  ) : (
                    <>
                      <Field.Root required>
                        <Field.Label>
                          Hospital
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Box
                          maxW={{
                            base: '100%',
                            md: 'calc(50% - 0.625rem)',
                          }}
                        >
                          <select
                            value={selectedHospitalId}
                            onChange={(event) =>
                              setSelectedHospitalId(
                                event.target.value,
                              )
                            }
                            style={{
                              width: '100%',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '0.375rem',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor:
                                'var(--tcm-colors-border-gray)',
                              backgroundColor:
                                'var(--tcm-colors-background-primary)',
                              fontSize: '1rem',
                              color: 'inherit',
                              cursor: 'pointer',
                            }}
                          >
                            {hospitals.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.name}
                              </option>
                            ))}
                          </select>
                        </Box>
                      </Field.Root>

                      <Grid
                        templateColumns={{
                          base: '1fr',
                          md: '1fr 1fr',
                        }}
                        gap={5}
                      >
                        <Field.Root required>
                          <Field.Label>
                            Patient Name
                            <Field.RequiredIndicator />
                          </Field.Label>
                          <Input
                            variant="outline"
                            css={{
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: 'border.gray',
                              borderRadius: 'md',
                              px: 3,
                              minH: 10,
                            }}
                            value={formValues.patientName}
                            placeholder="e.g. John Doe"
                            onChange={(event) =>
                              handleFieldChange(
                                'patientName',
                                event.target.value,
                              )
                            }
                            _focusVisible={{
                              borderColor: 'background.brandLight',
                              boxShadow:
                                '0 0 0 1px var(--tcm-colors-text-brandLight)',
                            }}
                          />
                        </Field.Root>

                        <Field.Root required>
                          <Field.Label>
                            Date of Birth
                            <Field.RequiredIndicator />
                          </Field.Label>
                          <Input
                            variant="outline"
                            css={{
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: 'border.gray',
                              borderRadius: 'md',
                              px: 3,
                              minH: 10,
                            }}
                            type="date"
                            value={formValues.dateOfBirth}
                            onChange={(event) =>
                              handleFieldChange(
                                'dateOfBirth',
                                event.target.value,
                              )
                            }
                            _focusVisible={{
                              borderColor: 'background.brandLight',
                              boxShadow:
                                '0 0 0 1px var(--tcm-colors-text-brandLight)',
                            }}
                          />
                        </Field.Root>
                      </Grid>

                      <Field.Root
                        required
                        maxW={{
                          base: '100%',
                          md: 'calc(50% - 0.625rem)',
                        }}
                      >
                        <Field.Label>
                          Estimated Discharge Date
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                          variant="outline"
                          css={{
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: 'border.gray',
                            borderRadius: 'md',
                            px: 3,
                            minH: 10,
                          }}
                          type="date"
                          value={formValues.estimatedDischargeDate}
                          onChange={(event) =>
                            handleFieldChange(
                              'estimatedDischargeDate',
                              event.target.value,
                            )
                          }
                          _focusVisible={{
                            borderColor: 'background.brandLight',
                            boxShadow:
                              '0 0 0 1px var(--tcm-colors-text-brandLight)',
                          }}
                        />
                      </Field.Root>

                      <Separator />

                      <Flex
                        justify="flex-end"
                        align="center"
                        gap={4}
                        wrap="wrap"
                      >
                        <Button
                          colorPalette="brand"
                          variant="solid"
                          css={{
                            bg: 'background.brandLight',
                            color: 'white',
                            px: 6,
                            minH: 10,
                            borderRadius: 'md',
                            fontWeight: 'bold',
                          }}
                          onClick={() => {
                            void handleLookup();
                          }}
                          loading={isSubmitting}
                          spinner={
                            <Box
                              w="0.95rem"
                              h="0.95rem"
                              borderRadius="full"
                              borderWidth="2px"
                              borderStyle="solid"
                              borderColor="rgba(255, 255, 255, 0.35)"
                              borderTopColor="var(--tcm-colors-text-white)"
                              css={{
                                animation:
                                  'tcm-spin 0.8s linear infinite',
                              }}
                              aria-label="Fetching patient data"
                            />
                          }
                          loadingText="Fetching patient data"
                          disabled={!canSubmit}
                        >
                          Fetch Patient Data
                        </Button>
                      </Flex>

                      {showPresetPatients ? (
                        <Card.Root
                          bg="background.primary"
                          border="1px solid"
                          borderColor="border.gray"
                          borderRadius="xl"
                          css={{
                            borderColor:
                              'var(--tcm-colors-border-gray)',
                          }}
                        >
                          <Card.Body>
                            <Stack gap={4}>
                              <HStack>
                                <Badge colorPalette="blue">
                                  Test users (QA & local only)
                                </Badge>
                              </HStack>
                              <Stack
                                direction={{
                                  base: 'column',
                                  sm: 'row',
                                }}
                                gap={3}
                                wrap="wrap"
                              >
                                {presetPatients.map((preset) => (
                                  <Button
                                    key={preset.patientName}
                                    variant="outline"
                                    borderRadius="lg"
                                    borderWidth="1px"
                                    borderColor="border.gray"
                                    css={{
                                      borderColor:
                                        'var(--tcm-colors-border-gray)',
                                    }}
                                    bg="background.primary"
                                    onClick={() =>
                                      handlePresetSelect(
                                        preset.patientName,
                                        preset.dateOfBirth,
                                      )
                                    }
                                    _hover={{
                                      borderColor: 'brand.500',
                                    }}
                                    alignSelf="flex-start"
                                    h="auto"
                                    py={2}
                                    px={3}
                                  >
                                    <Stack
                                      gap={0}
                                      align="flex-start"
                                      textAlign="left"
                                    >
                                      <Text
                                        fontWeight="semibold"
                                        fontSize="sm"
                                      >
                                        {preset.patientName}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color="text.secondary"
                                      >
                                        DOB {preset.dateOfBirthLabel}
                                      </Text>
                                    </Stack>
                                  </Button>
                                ))}
                              </Stack>
                            </Stack>
                          </Card.Body>
                        </Card.Root>
                      ) : null}
                    </>
                  )}
                </Stack>
                )
              ) : currentStep === 2 && patientCheckData ? (
                <PatientCheckStep
                  data={patientCheckData}
                  onGoBack={() => setCurrentStep(1)}
                  onContinue={() => {
                    skippedPatientVerificationRef.current = false;
                    setSkippedPatientVerification(false);
                    setProcessingItems(INITIAL_PROCESSING_ITEMS);
                    setIsReviewProcessing(true);
                    setCurrentStep(3);
                  }}
                  onContinueAnyway={() => {
                    skippedPatientVerificationRef.current = true;
                    setSkippedPatientVerification(true);
                    setProcessingItems(INITIAL_PROCESSING_ITEMS);
                    setIsReviewProcessing(true);
                    setCurrentStep(3);
                  }}
                />
              ) : currentStep === 3 ? (
                isReviewProcessing ? (
                  <ReviewProcessingStep
                    items={processingItems}
                    isProcessing={true}
                    providers={effectiveProviderOptions}
                    schedulingProvider={selectedProvider}
                    pcpProvider={pcpProvider}
                    pcpPractice={pcpPractice}
                    selectedProviderId={selectedProviderId}
                    onProviderIdChange={setSelectedProviderId}
                    selectedSlotId={selectedSlotId}
                    onSlotIdChange={setSelectedSlotId}
                    appointmentNotes={appointmentNotes}
                    onAppointmentNotesChange={setAppointmentNotes}
                    onBack={() => setCurrentStep(2)}
                    onContinue={() => setCurrentStep(4)}
                    onStartOver={handleStartOver}
                    contactMode={skippedPatientVerification}
                    pacPhone={providerSearchPacPhone}
                  />
                ) : (
                <ProviderSearchStep
                  providers={step3ProviderOptions}
                  selectedProviderId={selectedProviderId}
                  onProviderIdChange={(id) => {
                    setSelectedProviderId(id);
                    setSelectedSlotId('');
                  }}
                  onBack={() => setCurrentStep(2)}
                  onContinue={(confirmedKey) => {
                    const newPcpKey = confirmedKey || null;
                    setPcpProviderId(newPcpKey);
                    setSelectedSlotId('');
                    setCurrentStep(4);
                    const confirmedProvider = confirmedKey
                      ? resolveWizardProvider(step3ProviderOptions, confirmedKey)
                      : undefined;
                    if (confirmedProvider && selectedHospitalId) {
                      postSelectedPcp(apiClient, {
                        hospitalId: selectedHospitalId,
                        providerName: confirmedProvider.name,
                        providerNickname: confirmedProvider.nickname ?? null,
                        providerRmId: confirmedProvider.id,
                      }).catch(() => {});
                    }
                  }}
                  onStartOver={handleStartOver}
                  status={providerSearchStatus}
                  errorMessage={providerSearchErrorMessage}
                  pacPhone={providerSearchPacPhone}
                  showEstablishedPcpUi={!skippedPatientVerification}
                  patientNotAccepted={skippedPatientVerification}
                  patientNotFoundInAthena={
                    athenaPatient?.match_found === false
                  }
                  alreadyScheduled={alreadyScheduled}
                  patientName={athenaPatient?.full_name ?? submittedValues?.patientName ?? ''}
                />
                )
              ) : currentStep === 4 ? (
                <ReviewProcessingStep
                  items={processingItems}
                  isProcessing={false}
                  providers={effectiveProviderOptions}
                  schedulingProvider={selectedProvider}
                  pcpProvider={pcpProvider}
                  pcpPractice={pcpPractice}
                  selectedProviderId={selectedProviderId}
                  onProviderIdChange={setSelectedProviderId}
                  selectedSlotId={selectedSlotId}
                  onSlotIdChange={setSelectedSlotId}
                  appointmentNotes={appointmentNotes}
                  onAppointmentNotesChange={setAppointmentNotes}
                  onBack={() => setCurrentStep(3)}
                  onContinue={() => setCurrentStep(5)}
                  onStartOver={handleStartOver}
                  contactMode={skippedPatientVerification}
                  pacPhone={providerSearchPacPhone}
                  maxNotesLength={
                    !isNewPatient && pcpProvider && selectedProvider?.id !== pcpProvider.id
                      ? 500 - `This patient is cross-scheduled. Regular provider is ${pcpProvider.name}.\n`.length
                      : undefined
                  }
                />
              ) : (
                <Stack gap={8}>
                  <Flex align="flex-start" justify="space-between" gap={4}>
                    <Stack gap={2}>
                      <Text
                        color="text.brandDark"
                        fontSize="2xl"
                        fontWeight="700"
                      >
                        {currentStepDefinition.label}
                      </Text>
                      <Text color="text.secondary" fontSize="sm">
                        Review details and scheduling below.
                      </Text>
                    </Stack>
                    <Button
                      variant="ghost"
                      flexShrink={0}
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      px={4}
                      minH={10}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="background.brandLight"
                      bg="brand.50"
                      color="text.brandLight"
                      fontWeight="600"
                      fontSize="sm"
                      _hover={{
                        bg: 'background.brandLight',
                        color: 'text.white',
                      }}
                      css={{ transition: 'all 0.15s ease' }}
                      onClick={() =>
                        openPrintSummary({
                          patientName:
                            athenaPatient?.full_name ??
                            submittedValues?.patientName ??
                            'Patient',
                          providerName: selectedProvider?.name ?? '',
                          appointmentSlot: nextSlotForScheduling,
                          practice: selectedPractice,
                        })
                      }
                      aria-label="Print Summary"
                    >
                      <FiPrinter aria-hidden />
                      Print Summary
                    </Button>
                  </Flex>

                  <WizardSummary
                    submittedValues={submittedValues}
                    lookupResults={lookupResults}
                    athenaPatient={athenaPatient}
                  />

                  <Grid
                    templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
                    gap={6}
                    alignItems="start"
                  >
                    <Card.Root
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="border.subtle"
                      bg="background.primary"
                      boxShadow="sm"
                      overflow="hidden"
                    >
                      <Box
                        px={5}
                        py={3}
                        bg="blue.50"
                        borderBottomWidth="1px"
                        borderColor="border.subtle"
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color="text.brandDark"
                        >
                          Practice
                        </Text>
                      </Box>
                      <Card.Body p={5}>
                        <PracticeInfoPanel
                          practice={selectedPractice}
                          hideLabel
                          useBackline={false}
                        />
                      </Card.Body>
                    </Card.Root>

                    <Card.Root
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="border.subtle"
                      bg="background.primary"
                      boxShadow="sm"
                      overflow="hidden"
                    >
                      <Box
                        px={5}
                        py={3}
                        bg="blue.50"
                        borderBottomWidth="1px"
                        borderColor="border.subtle"
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color="text.brandDark"
                        >
                          Provider &amp; appointment time
                        </Text>
                      </Box>
                      <Card.Body p={5}>
                        <Stack gap={4}>
                          {selectedProvider ? (
                            <Stack gap={2}>
                              <HStack
                                justify="space-between"
                                align="start"
                                flexWrap="wrap"
                                gap={2}
                              >
                                <Text
                                  fontWeight="700"
                                  fontSize="lg"
                                  color="text.brandDark"
                                >
                                  {selectedProvider.name}
                                </Text>
                                {selectedProvider.nickname ? (
                                  <Text
                                    color="text.secondary"
                                    fontSize="sm"
                                  >
                                    {selectedProvider.nickname}
                                  </Text>
                                ) : null}
                                {!skippedPatientVerification &&
                                selectedProvider.isPatientPcp ? (
                                  <Badge
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    bg="purple.50"
                                    color="purple.800"
                                  >
                                    Established PCP
                                  </Badge>
                                ) : null}
                              </HStack>
                              <Text
                                color="text.secondary"
                                fontSize="sm"
                              >
                                {selectedProvider.specialty} ·{' '}
                                {selectedProvider.department}
                              </Text>
                              <Box>
                                <Text
                                  color="text.secondary"
                                  fontSize="sm"
                                  fontWeight="600"
                                >
                                  Hospital follow-up appointment type
                                </Text>
                                <Text
                                  color="text.brandDark"
                                  fontSize="sm"
                                  fontWeight="700"
                                  mt={1}
                                >
                                  {displayOrNone(
                                    selectedProvider.hospitalFollowUpAppointmentType,
                                  )}
                                </Text>
                              </Box>
                              <Badge
                                alignSelf="flex-start"
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                bg={
                                  selectedProvider.isEmployed
                                    ? 'green.100'
                                    : 'orange.100'
                                }
                                color={
                                  selectedProvider.isEmployed
                                    ? 'green.800'
                                    : 'orange.900'
                                }
                                fontSize="xs"
                              >
                                {selectedProvider.isEmployed
                                  ? 'Employed'
                                  : 'External'}
                              </Badge>
                            </Stack>
                          ) : (
                            <Text
                              color="text.secondary"
                              fontSize="sm"
                            >
                              No provider selected.
                            </Text>
                          )}
                          <Separator />
                          {nextSlotForScheduling ? (
                            <Stack gap={2}>
                              <Text
                                fontSize={{ base: 'xl', md: '2xl' }}
                                fontWeight="700"
                                color="text.brandDark"
                              >
                                {formatProviderSlotTime(
                                  nextSlotForScheduling,
                                )}
                              </Text>
                              {slotFollowUpWindowLabel(
                                selectedProvider,
                                nextSlotForScheduling,
                              ) ? (
                                <Badge
                                  alignSelf="flex-start"
                                  px={2}
                                  py={0.5}
                                  borderRadius="md"
                                  bg="blue.50"
                                  color="text.brandLight"
                                  fontSize="xs"
                                >
                                  {slotFollowUpWindowLabel(
                                    selectedProvider,
                                    nextSlotForScheduling,
                                  )}
                                </Badge>
                              ) : null}
                            </Stack>
                          ) : (
                            <Text
                              fontSize="sm"
                              color="text.secondary"
                            >
                              No appointment slot on record for this
                              summary.
                            </Text>
                          )}
                        </Stack>
                      </Card.Body>
                    </Card.Root>
                  </Grid>

                  {skippedPatientVerification ? (
                    <Card.Root
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor="blue.200"
                      bg="blue.50"
                    >
                      <Card.Body p={{ base: 5, md: 6 }}>
                        <Stack gap={3}>
                          <Text
                            fontWeight="700"
                            fontSize="lg"
                            color="text.brandDark"
                          >
                            Contact the practice
                          </Text>
                          {reviewContactPhone.trim() ? (
                            <Text
                              color="text.secondary"
                              fontSize="sm"
                            >
                              Call {reviewContactPhone} to schedule or
                              follow up. No appointment was booked
                              automatically because patient identity
                              was not confirmed.
                            </Text>
                          ) : (
                            <Text
                              color="text.secondary"
                              fontSize="sm"
                            >
                              No practice phone was found on the
                              practice, provider, or patient record.
                              Use your usual channels to reach the
                              practice.
                            </Text>
                          )}
                        </Stack>
                      </Card.Body>
                    </Card.Root>
                  ) : (
                    <Card.Root
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={
                        scheduleState === 'success'
                          ? 'green.200'
                          : scheduleState === 'error'
                            ? 'red.200'
                            : 'border.subtle'
                      }
                      bg={
                        scheduleState === 'success'
                          ? 'green.50'
                          : scheduleState === 'error'
                            ? 'red.50'
                            : 'background.surfaceTertiary'
                      }
                    >
                      <Card.Body p={{ base: 5, md: 6 }}>
                        <HStack gap={4} align="start">
                          {scheduleState === 'loading' ? (
                            <VisibleInlineSpinner
                              size="1.15rem"
                              mt={1}
                            />
                          ) : null}
                          <Stack gap={3} flex="1" align="stretch">
                            <Text
                              fontWeight="700"
                              fontSize="lg"
                              color="text.brandDark"
                            >
                              {scheduleState === 'loading'
                                ? 'Scheduling appointment…'
                                : scheduleState === 'success'
                                  ? 'Scheduling complete'
                                  : scheduleState === 'error'
                                    ? 'Scheduling failed'
                                    : 'Scheduling status'}
                            </Text>

                            {scheduleState === 'loading' ? (
                              <Text
                                color="text.secondary"
                                fontSize="sm"
                              >
                                Scheduling appointment with{' '}
                                <Text
                                  as="span"
                                  fontWeight="600"
                                  color="text.brandDark"
                                >
                                  {selectedProvider?.name ?? 'provider'}
                                </Text>
                                .
                              </Text>
                            ) : null}

                            {scheduleState === 'success' ? (
                              <Stack gap={2}>
                                <Text
                                  color="text.secondary"
                                  fontSize="sm"
                                >
                                  {scheduleResult?.message ??
                                    'The appointment has been scheduled successfully.'}
                                </Text>
                              </Stack>
                            ) : null}

                            {scheduleState === 'error' ? (
                              <Alert.Root
                                status="error"
                                borderRadius="lg"
                                size="sm"
                              >
                                <Alert.Indicator />
                                <Alert.Content>
                                  <Alert.Title>
                                    {scheduleError}
                                  </Alert.Title>
                                </Alert.Content>
                              </Alert.Root>
                            ) : null}

                            {scheduleState === 'idle' ? (
                              <Text
                                color="text.secondary"
                                fontSize="sm"
                              >
                                Continue to run scheduling.
                              </Text>
                            ) : null}
                          </Stack>
                        </HStack>
                      </Card.Body>
                    </Card.Root>
                  )}

                  <Separator />

                  <Stack gap={1}>
                    <Text color="text.brandDark" fontWeight="700">
                      Need to schedule more appointments?
                    </Text>
                    <Text color="text.secondary" fontSize="sm">
                      {providerSearchPacPhone?.trim() ? (
                        <>
                          Contact PAC at{' '}
                          <a
                            href={phoneHref(
                              providerSearchPacPhone.trim(),
                            )}
                            style={{
                              color:
                                'var(--tcm-colors-text-brandLight)',
                              textDecoration: 'underline',
                              fontWeight: 600,
                            }}
                          >
                            {formatPhoneForDisplay(
                              providerSearchPacPhone.trim(),
                            )}
                          </a>{' '}
                          or use the{' '}
                        </>
                      ) : (
                        <>Use the </>
                      )}
                      <a
                        href={AFTER_HOURS_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color:
                            'var(--tcm-colors-text-brandLight)',
                          textDecoration: 'underline',
                          fontWeight: 600,
                        }}
                      >
                        after-hours formstack
                      </a>
                      .
                    </Text>
                  </Stack>

                  <WizardActionBar
                    currentStep={currentStep}
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    onBack={goToPreviousStep}
                    onNext={goToNextStep}
                    onStartOver={handleStartOver}
                    startOverDisabled={scheduleState === 'loading'}
                    hideBack={hideSummaryBackButton}
                  />
                </Stack>
              )}
            </Card.Body>
          </Card.Root>
        </Stack>
        <StartOverConfirmModal
          open={isStartOverModalOpen}
          onCancel={() => setIsStartOverModalOpen(false)}
          onConfirm={confirmStartOver}
        />
        <WrapCodeModal
          open={isWrapCodeModalOpen}
          isPac={isPacUser()}
          onCancel={() => setIsWrapCodeModalOpen(false)}
          onSubmit={handleWrapCodeSubmit}
        />
      </Container>
    </Box>
  );
}
